import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  createAdminAcca,
  createAdminPick,
  deleteAdminAcca,
  deleteAdminPick,
  fetchAdminAccas,
  fetchAdminFeaturedFixtures,
  fetchAdminPickFixtures,
  fetchAdminPicks,
  fetchBaseballFixtureContext,
  fetchCommunityHome,
  fetchFeaturedFixtures,
  fetchPlayerOdds,
  notifyAdminFeaturedFixtures,
  publishAdminAcca,
  settleAdminAcca,
  updateAdminFeaturedFixtures,
} from "../api";
import { formatMarketName } from "../utils/formatMarketName";
import AdminFeaturedEmailPanel from "../components/AdminFeaturedEmailPanel";
import { isWorldCupCompetition } from "../utils/worldCup";

const todayISO = () => new Date().toISOString().slice(0, 10);

const SPORTS = [
  { value: "football", label: "Football" },
  { value: "rugby", label: "Rugby" },
  { value: "baseball", label: "MLB" },
  { value: "nfl", label: "NFL" },
  { value: "nhl", label: "NHL" },
  { value: "nba", label: "NBA" },
  { value: "cfb", label: "CFB" },
];

const BASEBALL_PROP_GROUPS = [
  ["hits", "Hits"],
  ["home_runs", "Home Runs"],
  ["rbi", "RBI"],
  ["runs", "Runs"],
  ["total_bases", "Total Bases"],
  ["singles", "Singles"],
  ["doubles", "Doubles"],
  ["triples", "Triples"],
  ["stolen_bases", "Stolen Bases"],
  ["pitcher_strikeouts", "Pitcher Strikeouts"],
  ["pitcher_outs", "Pitcher Outs"],
  ["pitcher_walks_issued", "Pitcher Walks Issued"],
  ["pitcher_hits_allowed", "Pitcher Hits Allowed"],
];

const BASEBALL_REVIEW_FILTERS = [
  { value: "all", label: "All" },
  { value: "game", label: "Game Edges" },
  { value: "props", label: "Player Prop Edges" },
];

const BASEBALL_CATEGORY_FILTERS = [
  { value: "all", label: "All Markets" },
  { value: "moneyline", label: "Moneyline" },
  { value: "totals", label: "Totals" },
  { value: "run_line", label: "Run Line" },
  { value: "hits", label: "Hits" },
  { value: "home_runs", label: "Home Runs" },
  { value: "rbi", label: "RBI" },
  { value: "pitcher_strikeouts", label: "Pitcher Strikeouts" },
  { value: "pitcher_outs", label: "Pitcher Outs" },
];

const FOOTBALL_PROP_MARKET_FILTERS = [
  { value: "all", label: "All" },
  { value: "booked", label: "Booked" },
  { value: "shots", label: "Shots" },
  { value: "shots_on_target", label: "Shots on Target" },
  { value: "fouls", label: "Fouls" },
  { value: "tackles", label: "Tackles" },
  { value: "assists", label: "Assists" },
];

const FOOTBALL_MARKET_LABELS = {
  booked: "To be booked",
  to_be_booked: "To be booked",
  yellow: "To be booked",
  shots: "Shots",
  shots_on_target: "Shots on Target",
  sot: "Shots on Target",
  fouls: "Fouls",
  tackles: "Tackles",
  assists: "Assists",
};

const FOOTBALL_PROP_FETCH_LIMIT = 2000;

const PANEL = {
  border: "1px solid rgba(148,163,184,0.2)",
  borderRadius: 16,
  background: "linear-gradient(180deg, rgba(8,20,17,0.96), rgba(6,13,12,0.96))",
  boxShadow: "0 12px 28px rgba(0,0,0,0.24)",
};

const CARD = {
  border: "1px solid rgba(148,163,184,0.16)",
  borderRadius: 14,
  background: "rgba(255,255,255,0.03)",
  boxShadow: "0 8px 18px rgba(0,0,0,0.14)",
};

const BTN = {
  height: 36,
  borderRadius: 10,
  border: "1px solid rgba(148,163,184,0.28)",
  background: "rgba(255,255,255,0.06)",
  color: "#e2f2e8",
  padding: "0 12px",
  fontWeight: 700,
  cursor: "pointer",
};

const BTN_PRIMARY = {
  ...BTN,
  border: "1px solid rgba(49,212,139,0.72)",
  background: "linear-gradient(180deg, #31d48b, #16b96b)",
  color: "#052014",
};

const INPUT = {
  height: 38,
  borderRadius: 10,
  border: "1px solid rgba(148,163,184,0.25)",
  background: "rgba(248,250,252,0.96)",
  color: "#08130f",
  padding: "0 10px",
};

const TEXTAREA = {
  borderRadius: 10,
  border: "1px solid rgba(148,163,184,0.25)",
  background: "rgba(248,250,252,0.96)",
  color: "#08130f",
  padding: "10px 12px",
};

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const toArray = (value) => (Array.isArray(value) ? value : []);

const normalizeSportKey = (value) => {
  const sport = String(value || "").trim().toLowerCase();
  if (sport === "mlb") return "baseball";
  if (sport === "soccer") return "football";
  return sport || "football";
};

const inferFixtureSport = (row = {}) => {
  const explicit = normalizeSportKey(firstDefined(row.sport, row.sport_key, row.league_sport, ""));
  if (explicit && explicit !== "football") return explicit;
  const comp = String(firstDefined(row.comp, row.competition, row.league_name, "")).toLowerCase();
  if (comp.includes("mlb") || comp.includes("baseball")) return "baseball";
  if (comp === "nba") return "nba";
  if (comp === "nhl") return "nhl";
  if (comp === "nfl") return "nfl";
  if (comp.includes("college") || comp === "ncaa") return "cfb";
  if (comp.includes("rugby") || comp === "top 14" || comp === "six nations") return "rugby";
  return explicit || "football";
};

const matchesSelectedSport = (row = {}, selectedSport = "") => {
  if (!selectedSport) return true;
  return inferFixtureSport(row) === selectedSport;
};

const slug = (s = "") =>
  s
    .normalize("NFKD")
    .replace(/[^\w\s.-]/g, "")
    .trim()
    .replace(/\s+/g, "_")
    .toLowerCase();

const logoUrl = (teamName) => `/logos/${slug(teamName)}.png`;

const fmtUK = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: "Europe/London",
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const fmtDayName = (day) => {
  try {
    return new Date(`${day}T12:00:00Z`).toLocaleDateString("en-GB", {
      weekday: "long",
      day: "2-digit",
      month: "short",
    });
  } catch {
    return day;
  }
};

const normalizeMarketKey = (value = "") => {
  let key = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^player_/, "")
    .replace(/\s+/g, "_");
  key = key.replace(/__(?=.)/g, "_");
  if (key.endsWith("_over")) return { groupKey: key.slice(0, -5), side: "over" };
  if (key.endsWith("_under")) return { groupKey: key.slice(0, -6), side: "under" };
  return { groupKey: key, side: "" };
};

const footballPropGroup = (marketKey = "", marketLabel = "") => {
  const key = String(marketKey || "").trim().toLowerCase();
  const label = String(marketLabel || "").trim().toLowerCase();
  const haystack = [key, label].filter(Boolean).join(" ");
  if (!haystack) return "other";
  if (
    ["booked", "to_be_booked", "yellow"].includes(key) ||
    haystack.includes("booked") ||
    haystack.includes("yellow") ||
    haystack.includes("to be booked")
  ) {
    return "booked";
  }
  if (
    ["shots_on_target", "sot"].includes(key) ||
    haystack.includes("shots_on_target") ||
    haystack.includes("shot on target") ||
    haystack.includes("shots on target") ||
    haystack.includes("sot")
  ) {
    return "shots_on_target";
  }
  if (haystack.includes("shots")) return "shots";
  if (haystack.includes("fouls")) return "fouls";
  if (haystack.includes("tackles")) return "tackles";
  if (haystack.includes("assists")) return "assists";
  return key || label || "other";
};

const formatFootballPropMarket = (marketKey = "", marketLabel = "") =>
  firstDefined(
    marketLabel && footballPropGroup(marketKey, marketLabel) === "booked" ? "To Be Booked" : null,
    FOOTBALL_MARKET_LABELS[footballPropGroup(marketKey, marketLabel)],
    String(marketKey || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase()),
    String(marketLabel || "")
      .replace(/\b\w/g, (char) => char.toUpperCase()),
    "Player Prop"
  );

const formatFootballPropSide = (marketKey = "", line = null, sideLabel = null, marketLabel = "") => {
  if (sideLabel) return sideLabel;
  const key = String(marketKey || "").trim().toLowerCase();
  const group = footballPropGroup(marketKey, marketLabel);
  const numericLine = normalizePrice(line);
  if (group === "booked") {
    return numericLine != null ? `Over ${numericLine}` : "Yes";
  }
  if (key.endsWith("_over")) return numericLine != null ? `Over ${numericLine}` : "Over";
  if (key.endsWith("_under")) return numericLine != null ? `Under ${numericLine}` : "Under";
  return numericLine != null ? `Over ${numericLine}` : "Line";
};

const footballMarketFilterToApiMarket = (filterValue = "all") => {
  const key = String(filterValue || "all").trim().toLowerCase();
  if (key === "booked") return "yellow";
  if (key === "shots_on_target") return "sot";
  if (["shots", "fouls", "tackles", "assists"].includes(key)) return key;
  return undefined;
};

const formatBaseballPropMarket = (marketKey = "") =>
  firstDefined(
    BASEBALL_PROP_GROUPS.find(([key]) => key === normalizeMarketKey(marketKey).groupKey)?.[1],
    String(marketKey || "")
      .replace(/^player_/, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase()),
    "Prop"
  );

const formatBaseballPropSide = (marketKey = "", line = null, sideLabel = null) => {
  if (sideLabel) return sideLabel;
  const { side } = normalizeMarketKey(marketKey);
  const human = side ? `${side.charAt(0).toUpperCase()}${side.slice(1)}` : "Line";
  return line != null ? `${human} ${line}` : human;
};

const formatEdgePercent = (edge) => {
  const number = Number(edge);
  if (!Number.isFinite(number)) return "—";
  const percent = number * 100;
  return `${percent > 0 ? "+" : ""}${percent.toFixed(1)}%`;
};

const formatModelPercent = (probability) => {
  const number = Number(probability);
  if (!Number.isFinite(number)) return "—";
  return `${(number * 100).toFixed(1)}%`;
};

const edgeTone = (edge) => {
  const number = Number(edge);
  if (!Number.isFinite(number)) return "#9ab3a5";
  if (number > 0.01) return "#86efac";
  if (number < -0.01) return "#fca5a5";
  return "#cbd5e1";
};

const normalizePrice = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const playerHeadshot = (playerId) =>
  playerId ? `https://img.mlbstatic.com/mlb-photos/image/upload/w_180,q_auto:best/v1/people/${playerId}/headshot/67/current` : "";

const normalizePlayerOddsRows = (payload) => {
  const rows = toArray(
    firstDefined(
      payload?.rows,
      payload?.data?.rows,
      payload?.player_odds,
      Array.isArray(payload) ? payload : []
    )
  );

  return rows.map((row) => {
    const marketKey = firstDefined(row.market, row.market_type, row.market_key, "");
    const marketLabel = firstDefined(row.market_label, row.label, row.market_name, "");
    const parsed = normalizeMarketKey(marketKey);
    const footballGroupKey = footballPropGroup(marketKey, marketLabel);
    const isFootballStyle = footballGroupKey !== "other";
    return {
      id: firstDefined(row.id, `${firstDefined(row.player_id, row.player_name, "player")}-${marketKey}-${firstDefined(row.line, row.bookmaker, "row")}`),
      raw: row,
      playerId: firstDefined(row.player_id, row.provider_player_id, row.participant_id, null),
      playerName: firstDefined(row.player_name, row.player, row.name, "Unknown Player"),
      market: marketKey,
      marketLabel: firstDefined(
        marketLabel,
        isFootballStyle ? formatFootballPropMarket(marketKey, marketLabel) : formatBaseballPropMarket(marketKey)
      ),
      side: parsed.side,
      sideLabel: firstDefined(
        row.side_label,
        isFootballStyle
          ? formatFootballPropSide(marketKey, firstDefined(row.line, row.handicap, row.total, null), row.side_label, marketLabel)
          : formatBaseballPropSide(marketKey, firstDefined(row.line, row.handicap, row.total, null))
      ),
      line: firstDefined(row.line, row.handicap, row.total, null),
      bookmaker: firstDefined(row.bookmaker, row.book, "—"),
      price: normalizePrice(firstDefined(row.price, row.odds, null)),
      modelProb: normalizePrice(firstDefined(row.model_prob, row.model_probability, null)),
      fairPrice: normalizePrice(firstDefined(row.fair_price, row.fair_odds, null)),
      edge: normalizePrice(firstDefined(row.edge, row.edge_pct, row.edge_percent, null)),
      expectedMean: normalizePrice(firstDefined(row.expected_mean, row.mean, row.projected_mean, null)),
      plainSummary: firstDefined(row.plain_summary, row.summary, null),
      why: firstDefined(row.why, row.explanation, null),
      keyFactors: toArray(firstDefined(row.key_factors, row.factors, [])),
      modelSource: firstDefined(row.model_source, row.source, null),
      groupKey: parsed.groupKey,
      footballGroup: footballGroupKey,
      teamId: firstDefined(row.team_id, null),
      teamName: firstDefined(row.team_name, row.team, null),
      photo: firstDefined(row.player_photo, row.photo, row.meta?.photo, null),
      lastSeen: firstDefined(row.last_seen, row.seen_at, row.updated_at, row.ts, null),
    };
  });
};

const mergePropRows = (rows = []) => {
  const merged = {};
  rows.forEach((row, index) => {
    const key = `${row.groupKey}|${row.playerId || row.playerName}|${row.line ?? ""}|${row.bookmaker}`;
    if (!merged[key]) {
      merged[key] = {
        id: `${key || "prop"}-${index}`,
        groupKey: row.groupKey,
        playerId: row.playerId,
        playerName: row.playerName,
        teamId: row.teamId,
        teamName: row.teamName,
        market: row.market,
        marketLabel: row.marketLabel,
        line: row.line,
        bookmaker: row.bookmaker,
        overPrice: null,
        underPrice: null,
        overData: null,
        underData: null,
        bestEdge: null,
        bestSide: "",
        bestData: null,
      };
    }
    if (row.side === "under") {
      merged[key].underPrice = row.price;
      merged[key].underData = row;
    } else {
      merged[key].overPrice = row.price;
      merged[key].overData = row;
    }
    [merged[key].overData, merged[key].underData].forEach((candidate) => {
      if (!candidate || candidate.edge == null) return;
      if (merged[key].bestEdge == null || candidate.edge > merged[key].bestEdge) {
        merged[key].bestEdge = candidate.edge;
        merged[key].bestSide = candidate.side || "over";
        merged[key].bestData = candidate;
      }
    });
  });
  return Object.values(merged).sort((a, b) => {
    const edgeGap = Number(b.bestEdge || -999) - Number(a.bestEdge || -999);
    if (edgeGap) return edgeGap;
    return String(a.playerName).localeCompare(String(b.playerName));
  });
};

const summarizeBooks = (rows = []) =>
  Array.from(new Set(rows.map((row) => row.bookmaker).filter(Boolean))).slice(0, 4).join(", ");

const baseballGameEdgeCategory = (edge = {}) => {
  const market = String(firstDefined(edge.market_category, edge.market_type, edge.market, edge.label, "")).toLowerCase();
  if (market.includes("moneyline") || market.includes("ml")) return "moneyline";
  if (market.includes("total") || market.includes("over") || market.includes("under")) return "totals";
  if (market.includes("run") || market.includes("spread") || market.includes("handicap")) return "run_line";
  return "other";
};

const isSupportedBaseballGameEdge = (edge = {}) => {
  const market = String(firstDefined(edge.market, edge.market_type, edge.market_key, "")).toUpperCase();
  const category = baseballGameEdgeCategory(edge);
  if (category !== "run_line") return true;
  // Old generic SPREAD_HOME/SPREAD_AWAY rows did not retain the actual handicap.
  // They cannot be reviewed safely and must not appear as actionable MLB edges.
  return /^RUN_LINE_(HOME|AWAY)_[PN]\d+(?:_\d+)?$/.test(market);
};

const baseballPropCategory = (row = {}) => normalizeMarketKey(row.market).groupKey || "other";

const formatBaseballGameEdgeLabel = (fixture, edge = {}) => {
  if (edge.market_label && edge.side_label) return `${edge.market_label} — ${edge.side_label}`;
  if (edge.market_label) return edge.market_label;
  const market = String(firstDefined(edge.market, edge.market_type, edge.market_key, "")).toLowerCase();
  const line = firstDefined(edge.line, edge.handicap, edge.total, null);
  if (market.includes("moneyline") || market === "home_ml" || market === "away_ml") {
    if (market.includes("away")) return `${fixture.away_team} Moneyline`;
    if (market.includes("home")) return `${fixture.home_team} Moneyline`;
    return "Moneyline";
  }
  if (market.includes("run") || market.includes("spread") || market.includes("handicap")) {
    if (market.includes("away")) return `${fixture.away_team} Run Line ${line != null ? line : ""}`.trim();
    if (market.includes("home")) return `${fixture.home_team} Run Line ${line != null ? line : ""}`.trim();
    return line != null ? `Run Line ${line}` : "Run Line";
  }
  if (market.includes("over")) return `Game Total — Over ${line != null ? line : ""}`.trim();
  if (market.includes("under")) return `Game Total — Under ${line != null ? line : ""}`.trim();
  return formatMarketName(edge.market);
};

const explicitBaseballPropLabel = ({ playerName, marketLabel, sideLabel, market, line }) => {
  const resolvedSide = sideLabel || formatBaseballPropSide(market, line);
  const resolvedMarket = marketLabel || formatBaseballPropMarket(market);
  if (playerName) return `${playerName} — ${resolvedMarket} — ${resolvedSide}`;
  return `${resolvedMarket} — ${resolvedSide}`;
};

const propPrefillNote = (row) => {
  const base = explicitBaseballPropLabel(row);
  const summary = row.plainSummary ? ` ${row.plainSummary}` : "";
  return `MLB Prop: ${base}.${summary}`.trim();
};

const footballPropCardTone = (edge) => {
  const number = Number(edge);
  if (!Number.isFinite(number)) {
    return {
      color: "#94a3b8",
      borderColor: "rgba(148,163,184,0.14)",
      background: "rgba(255,255,255,0.03)",
      opacity: 1,
    };
  }
  if (number > 0.03) {
    return {
      color: "#86efac",
      borderColor: "rgba(34,197,94,0.34)",
      background: "rgba(34,197,94,0.08)",
      opacity: 1,
    };
  }
  if (number > 0.005) {
    return {
      color: "#bbf7d0",
      borderColor: "rgba(110,231,183,0.22)",
      background: "rgba(255,255,255,0.03)",
      opacity: 1,
    };
  }
  if (number < -0.03) {
    return {
      color: "#94a3b8",
      borderColor: "rgba(148,163,184,0.12)",
      background: "rgba(255,255,255,0.02)",
      opacity: 0.58,
    };
  }
  return {
    color: "#cbd5e1",
    borderColor: "rgba(148,163,184,0.14)",
    background: "rgba(255,255,255,0.03)",
    opacity: 0.82,
  };
};

const featuredPickMarketLabel = (pick) => {
  if (String(pick?.pick_type || "").toLowerCase() === "player_prop") {
    if (footballPropGroup(pick?.market, pick?.market_label)) {
      return [
        firstDefined(pick.market_label, formatFootballPropMarket(pick.market, pick.market_label)),
        firstDefined(pick.side_label, formatFootballPropSide(pick.market, pick.line, pick.side_label, pick.market_label)),
      ]
        .filter(Boolean)
        .join(" — ") || formatMarketName(pick.market);
    }
    return firstDefined(
      pick.market_label || pick.side_label ? explicitBaseballPropLabel({
        playerName: pick.player_name,
        marketLabel: pick.market_label,
        sideLabel: pick.side_label,
        market: pick.market,
        line: pick.line,
      }) : null,
      pick.market_label,
      pick.side_label,
      formatBaseballPropMarket(pick.market)
    );
  }
  if (String(pick?.sport).toLowerCase() !== "baseball") return formatMarketName(pick.market);
  const raw = String(pick?.market || "");
  if (raw.startsWith("player_") || raw.startsWith("pitcher_")) {
    return explicitBaseballPropLabel({
      playerName: pick.player_name,
      market: raw,
      marketLabel: pick.market_label,
      sideLabel: pick.side_label,
      line: pick?.line,
    });
  }
  return formatMarketName(pick.market);
};

const combinedOdds = (legs) => {
  if (!legs.length) return null;
  const total = legs.reduce((product, leg) => product * Number(leg.price || 1), 1);
  return Number.isFinite(total) ? total : null;
};

const activeCommunityCount = (rows) =>
  rows.filter((row) => {
    const comments = Number(firstDefined(row.comments_total, row.comment_count, 0));
    const polls = Number(firstDefined(row.poll_votes_total, row.votes_total, row.poll_total, 0));
    const likes = Number(firstDefined(row.fixture_likes_total, row.likes_total, 0));
    const motm = Number(firstDefined(row.motm_votes_total, row.motm_total, 0));
    return comments > 0 || polls > 0 || likes > 0 || motm > 0;
  }).length;

function TeamChip({ name, align = "left" }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      {align === "left" ? (
        <img
          loading="lazy"
          src={logoUrl(name)}
          alt=""
          width={18}
          height={18}
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
      ) : null}
      <b style={{ fontWeight: 700 }}>{name || "—"}</b>
      {align === "right" ? (
        <img
          loading="lazy"
          src={logoUrl(name)}
          alt=""
          width={18}
          height={18}
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
      ) : null}
    </span>
  );
}

function Badge({ children, tone = "default" }) {
  const palette =
    tone === "green"
      ? {
          border: "1px solid rgba(74,222,128,0.42)",
          background: "rgba(34,197,94,0.12)",
          color: "#b8f4c9",
        }
      : tone === "amber"
      ? {
          border: "1px solid rgba(251,191,36,0.34)",
          background: "rgba(245,158,11,0.12)",
          color: "#fde68a",
        }
      : tone === "blue"
      ? {
          border: "1px solid rgba(56,189,248,0.36)",
          background: "rgba(14,165,233,0.12)",
          color: "#bae6fd",
        }
      : tone === "red"
      ? {
          border: "1px solid rgba(248,113,113,0.38)",
          background: "rgba(239,68,68,0.12)",
          color: "#fecaca",
        }
      : {
          border: "1px solid rgba(148,163,184,0.26)",
          background: "rgba(255,255,255,0.05)",
          color: "#dbe7df",
        };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        borderRadius: 999,
        padding: "3px 9px",
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: 0.25,
        ...palette,
      }}
    >
      {children}
    </span>
  );
}

function SummaryStat({ label, value, hint }) {
  return (
    <div
      style={{
        ...CARD,
        padding: 12,
      }}
    >
      <div style={{ fontSize: 11, color: "#9bb1a7", textTransform: "uppercase", letterSpacing: 0.7 }}>
        {label}
      </div>
      <div style={{ marginTop: 6, fontSize: 24, fontWeight: 800, color: "#effaf2" }}>{value}</div>
      {hint ? <div style={{ marginTop: 4, fontSize: 12, color: "#8ca297" }}>{hint}</div> : null}
    </div>
  );
}

const normalizeFixture = (row = {}) => {
  const fixture = {
    id: firstDefined(row.id, row.fixture_id),
    sport: inferFixtureSport(row),
    comp: firstDefined(row.comp, row.competition, row.league_name, row.fixture?.comp, row.fixture?.league_name, "Unknown competition"),
    league_code: firstDefined(row.league_code, row.comp_code, row.competition_code, row.fixture?.league_code),
    league_id: firstDefined(row.league_id, row.provider_league_id, row.api_league_id, row.fixture?.league_id, row.fixture?.provider_league_id),
    home_team: firstDefined(row.home_team, row.home, row.fixture?.home_team, "Home"),
    away_team: firstDefined(row.away_team, row.away, row.fixture?.away_team, "Away"),
    kickoff_utc: firstDefined(row.kickoff_utc, row.kickoff, row.starts_at),
  };
  return {
    ...fixture,
    is_world_cup: isWorldCupCompetition(fixture),
  };
};

const gamePickLabels = (fixture, market) => {
  const normalized = String(market || "").trim().toUpperCase();
  const isMatchResult = ["HOME_WIN", "AWAY_WIN", "DRAW"].includes(normalized);
  const sideByMarket = {
    HOME_WIN: fixture.home_team,
    AWAY_WIN: fixture.away_team,
    DRAW: "Draw",
    BTTS_Y: "Yes",
    BTTS_YES: "Yes",
    BTTS_N: "No",
    BTTS_NO: "No",
  };
  return {
    market_label: isMatchResult ? "Match Result" : formatMarketName(normalized) || normalized,
    side_label: sideByMarket[normalized] || formatMarketName(normalized) || normalized,
  };
};

const normalizePick = (row = {}) => ({
  pick_id: firstDefined(row.pick_id, row.id),
  fixture_id: firstDefined(row.fixture_id, row.fixture?.id),
  sport: inferFixtureSport(row),
  pick_type: String(firstDefined(row.pick_type, "game")).toLowerCase(),
  player_id: firstDefined(row.player_id, null),
  player_name: firstDefined(row.player_name, row.player?.name, null),
  player_photo: firstDefined(row.player_photo, row.player?.photo, null),
  player_team_id: firstDefined(row.player_team_id, row.team_id, row.player_team?.id, null),
  player_team_name: firstDefined(row.player_team_name, row.team_name, row.player_team?.name, null),
  market: String(firstDefined(row.market, row.selection, "")).trim(),
  market_label: firstDefined(row.market_label, null),
  side_label: firstDefined(row.side_label, null),
  line: firstDefined(row.line, null),
  bookmaker: firstDefined(row.bookmaker, row.book, "bet365"),
  price: Number(firstDefined(row.price, row.odds, 0)),
  edge: normalizePrice(firstDefined(row.edge, row.edge_pct, row.edge_percent, null)),
  model_prob: normalizePrice(firstDefined(row.model_prob, row.model_probability, null)),
  fair_price: normalizePrice(firstDefined(row.fair_price, row.fair_odds, null)),
  result: String(firstDefined(row.result, row.status, "")).toLowerCase(),
  note: firstDefined(row.note, row.admin_note, null),
  is_premium_only: !!firstDefined(row.is_premium_only, row.premium_only, false),
  home_team: firstDefined(row.home_team, row.fixture?.home_team),
  away_team: firstDefined(row.away_team, row.fixture?.away_team),
  comp: firstDefined(row.comp, row.fixture?.comp, row.competition),
  kickoff_utc: firstDefined(row.kickoff_utc, row.fixture?.kickoff_utc),
});

const normalizeAcca = (row = {}) => ({
  id: firstDefined(row.id, row.ticket_id),
  title: firstDefined(row.title, "Featured Acca"),
  note: firstDefined(row.note, null),
  combined_price: Number(firstDefined(row.combined_price, row.price, 0)),
  stake_units: firstDefined(row.stake_units, row.stake, "—"),
  is_public: !!firstDefined(row.is_public, row.public, false),
  published_at: firstDefined(row.published_at, row.created_at, null),
  legs: toArray(firstDefined(row.legs, row.acca_legs, [])),
});

function FeaturedFixtureRow({ fixture, order, selected, onToggle }) {
  return (
    <div
      style={{
        ...CARD,
        padding: 12,
        borderColor: selected ? "rgba(49,212,139,0.58)" : "rgba(148,163,184,0.16)",
        background: selected ? "rgba(22,185,107,0.12)" : "rgba(255,255,255,0.03)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#f0faf4" }}>
            <TeamChip name={fixture.home_team} /> <span style={{ color: "#87a093" }}>vs</span> <TeamChip name={fixture.away_team} align="right" />
          </div>
          <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
            {fixture.is_world_cup ? <Badge tone="amber">WORLD CUP</Badge> : null}
            <Badge tone="blue">{fixture.sport.toUpperCase()}</Badge>
            <Badge>{fixture.comp}</Badge>
            <Badge>{fmtUK(fixture.kickoff_utc)}</Badge>
          </div>
        </div>
        <div style={{ display: "grid", gap: 8, justifyItems: "end" }}>
          {order > 0 ? <Badge tone="green">#{order}</Badge> : null}
          <button style={selected ? BTN_PRIMARY : BTN} type="button" onClick={() => onToggle(fixture.id)}>
            {selected ? "Selected" : "Add to Featured 5"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPicks() {
  const [day, setDay] = useState(todayISO());
  const [sport, setSport] = useState("football");

  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [featuredSaving, setFeaturedSaving] = useState(false);
  const [featuredNotifying, setFeaturedNotifying] = useState(false);
  const [featuredErr, setFeaturedErr] = useState("");
  const [featuredNotice, setFeaturedNotice] = useState("");
  const [pickerFixtures, setPickerFixtures] = useState([]);
  const [selectedFeatured, setSelectedFeatured] = useState([]);
  const [publicFeaturedRows, setPublicFeaturedRows] = useState([]);
  const [predictionLeaderboard, setPredictionLeaderboard] = useState([]);

  const [fixtures, setFixtures] = useState([]);
  const [picks, setPicks] = useState([]);
  const [accas, setAccas] = useState([]);
  const [communityHome, setCommunityHome] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [busyPickId, setBusyPickId] = useState(null);
  const [busyAccaId, setBusyAccaId] = useState(null);
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [pickTypeFilter, setPickTypeFilter] = useState("all");

  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});
  const [bookFilter, setBookFilter] = useState("all");
  const [baseballReviewFilter, setBaseballReviewFilter] = useState("all");
  const [baseballCategoryFilter, setBaseballCategoryFilter] = useState("all");
  const [baseballPositiveOnly, setBaseballPositiveOnly] = useState(true);
  const [footballMarketFilter, setFootballMarketFilter] = useState("all");
  const [footballPositiveOnly, setFootballPositiveOnly] = useState(true);
  const [footballBet365Only, setFootballBet365Only] = useState(false);
  const [footballModelOnly, setFootballModelOnly] = useState(false);
  const [footballShowNegative, setFootballShowNegative] = useState(false);
  const [footballMinEdge, setFootballMinEdge] = useState("0");
  const [footballDetailTab, setFootballDetailTab] = useState("match");
  const [propFeatureDraft, setPropFeatureDraft] = useState(null);
  const [openPropWhy, setOpenPropWhy] = useState({});
  const [loadedFootballPropByFixture, setLoadedFootballPropByFixture] = useState({});
  const [selectedFootballPropPreview, setSelectedFootballPropPreview] = useState({});
  const pickFormRefs = useRef({});

  const [builderTitle, setBuilderTitle] = useState("");
  const [builderNote, setBuilderNote] = useState("");
  const [builderStakeUnits, setBuilderStakeUnits] = useState("1");
  const [builderIsPublic, setBuilderIsPublic] = useState(true);
  const [builderPublishNow, setBuilderPublishNow] = useState(true);
  const [builderSubmitting, setBuilderSubmitting] = useState(false);
  const [builderErr, setBuilderErr] = useState("");
  const [builderOk, setBuilderOk] = useState("");
  const [starredPickIds, setStarredPickIds] = useState([]);

  const featuredIds = useMemo(
    () => selectedFeatured.map((row) => String(row.id)).filter(Boolean),
    [selectedFeatured]
  );

  const featuredIdSet = useMemo(() => new Set(featuredIds), [featuredIds]);

  const fixtureMap = useMemo(() => {
    const map = {};
    pickerFixtures.forEach((fixture) => {
      map[String(fixture.id)] = fixture;
    });
    fixtures.forEach((fixture) => {
      map[String(fixture.id)] = normalizeFixture(fixture);
    });
    selectedFeatured.forEach((fixture) => {
      map[String(fixture.id)] = fixture;
    });
    return map;
  }, [fixtures, pickerFixtures, selectedFeatured]);

  const picksByFixture = useMemo(() => {
    const map = {};
    picks.forEach((pick) => {
      const key = String(pick.fixture_id);
      if (!map[key]) map[key] = [];
      map[key].push(pick);
    });
    return map;
  }, [picks]);

  const builderPicks = useMemo(() => {
    const order = new Map(starredPickIds.map((id, idx) => [String(id), idx]));
    return picks
      .filter((pick) => order.has(String(pick.pick_id)))
      .sort((a, b) => order.get(String(a.pick_id)) - order.get(String(b.pick_id)));
  }, [picks, starredPickIds]);

  const builderCombinedOdds = useMemo(() => combinedOdds(builderPicks), [builderPicks]);

  const featuredCoverage = useMemo(
    () =>
      selectedFeatured.filter((fixture) => {
        const rows = picksByFixture[String(fixture.id)] || [];
        return rows.length > 0;
      }).length,
    [picksByFixture, selectedFeatured]
  );

  const communityActiveCount = useMemo(
    () => activeCommunityCount(publicFeaturedRows),
    [publicFeaturedRows]
  );

  const visibleFixtures = useMemo(
    () =>
      fixtures.filter((fixture) =>
        featuredOnly ? featuredIdSet.has(String(fixture.id)) : true
      ),
    [featuredIdSet, featuredOnly, fixtures]
  );

  const visiblePicks = useMemo(
    () =>
      picks.filter((pick) => {
        if (featuredOnly && !featuredIdSet.has(String(pick.fixture_id))) return false;
        if (pickTypeFilter === "game" && pick.pick_type === "player_prop") return false;
        if (pickTypeFilter === "player_prop" && pick.pick_type !== "player_prop") return false;
        return true;
      }),
    [featuredIdSet, featuredOnly, pickTypeFilter, picks]
  );

  const isBaseballFixture = useCallback((fixture = {}) => {
    const sportKey = inferFixtureSport(fixture);
    const comp = String(firstDefined(fixture.comp, fixture.competition, "")).toLowerCase();
    return sportKey === "baseball" || comp.includes("mlb") || comp.includes("baseball");
  }, []);

  const loadFeaturedFixtures = useCallback(async () => {
    setFeaturedLoading(true);
    setFeaturedErr("");
    setFeaturedNotice("");
    try {
      const data = await fetchAdminFeaturedFixtures(day);
      const featuredRows = toArray(
        firstDefined(data?.featured_fixtures, data?.featured, data?.fixtures, [])
      ).map(normalizeFixture);
      const pickerRows = toArray(
        firstDefined(data?.picker_fixtures, data?.available_fixtures, data?.fixtures_for_day, [])
      ).map(normalizeFixture);

      setSelectedFeatured(featuredRows.filter((row) => matchesSelectedSport(row, sport)).slice(0, 5));
      setPickerFixtures(pickerRows.filter((row) => matchesSelectedSport(row, sport)));
    } catch (e) {
      setFeaturedErr(e?.response?.data?.detail || e?.message || "Could not load featured community fixtures.");
      setSelectedFeatured([]);
      setPickerFixtures([]);
    } finally {
      setFeaturedLoading(false);
    }
  }, [day, sport]);

  const loadFeaturedPublicSummary = useCallback(async () => {
    try {
      const data = await fetchFeaturedFixtures(day);
      const rows = toArray(
        firstDefined(data?.featured_fixtures, data?.fixtures, data?.featured, [])
      );
      const leaderboard = toArray(
        firstDefined(data?.prediction_leaderboard, data?.leaderboard, data?.predictions_leaderboard, [])
      );
      setPublicFeaturedRows(rows);
      setPredictionLeaderboard(leaderboard);
    } catch {
      setPublicFeaturedRows([]);
      setPredictionLeaderboard([]);
    }
  }, [day]);

  const loadWorkflowData = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [fixtureData, pickData, accaData, homeData] = await Promise.all([
        fetchAdminPickFixtures({ day, sport }),
        fetchAdminPicks({ day, sport }),
        fetchAdminAccas({ day }),
        fetchCommunityHome(),
      ]);

      const fixtureRows = toArray(firstDefined(fixtureData?.fixtures, fixtureData?.rows, fixtureData)).map(normalizeFixture);
      const pickRows = toArray(firstDefined(pickData?.picks, pickData?.rows, pickData)).map(normalizePick);
      const accaRows = toArray(firstDefined(accaData?.accas, accaData?.rows, accaData)).map(normalizeAcca);

      setFixtures(fixtureRows.filter((row) => matchesSelectedSport(row, sport)));
      setPicks(pickRows.filter((row) => matchesSelectedSport(row, sport)));
      setAccas(accaRows);
      setCommunityHome(homeData || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Could not load admin picks workflow.");
      setFixtures([]);
      setPicks([]);
      setAccas([]);
      setCommunityHome(null);
    } finally {
      setLoading(false);
    }
  }, [day, sport]);

  useEffect(() => {
    loadFeaturedFixtures();
    loadFeaturedPublicSummary();
  }, [loadFeaturedFixtures, loadFeaturedPublicSummary]);

  useEffect(() => {
    loadWorkflowData();
  }, [loadWorkflowData]);

  useEffect(() => {
    setStarredPickIds((prev) =>
      prev.filter((pickId) => picks.some((pick) => String(pick.pick_id) === String(pickId)))
    );
  }, [picks]);

  const ensureDetail = async (fixtureId) => {
    if (detail[fixtureId]) return;
    try {
      const fixture = fixtureMap[String(fixtureId)] || {};
      if (isBaseballFixture(fixture)) {
        const [jsonResult, contextResult, propsResult] = await Promise.allSettled([
          api.get(`/api/fixtures/id/${fixtureId}/json`),
          fetchBaseballFixtureContext(fixtureId),
          fetchPlayerOdds({ fixtureId }),
        ]);
        const propsPayload = propsResult.status === "fulfilled" ? propsResult.value : { rows: [] };
        setDetail((prev) => ({
          ...prev,
          [fixtureId]: {
            ...(jsonResult.status === "fulfilled" ? jsonResult.value?.data || {} : {}),
            baseballContext: contextResult.status === "fulfilled" ? contextResult.value : null,
            playerOddsMeta: propsPayload,
            playerOddsRows: normalizePlayerOddsRows(propsPayload),
            error:
              jsonResult.status !== "fulfilled" &&
              contextResult.status !== "fulfilled" &&
              propsResult.status !== "fulfilled"
                ? "Could not load MLB edge detail."
                : "",
          },
        }));
        return;
      }

      if (inferFixtureSport(fixture) === "football") {
        const [jsonResult, propsResult] = await Promise.allSettled([
          api.get(`/api/fixtures/id/${fixtureId}/json`),
          fetchPlayerOdds({ fixtureId, limit: FOOTBALL_PROP_FETCH_LIMIT }),
        ]);
        const propsPayload = propsResult.status === "fulfilled" ? propsResult.value : { rows: [] };
        const normalizedRows = normalizePlayerOddsRows(propsPayload);
        setDetail((prev) => ({
          ...prev,
          [fixtureId]: {
            ...(jsonResult.status === "fulfilled" ? jsonResult.value?.data || {} : {}),
            playerOddsMeta: propsPayload,
            playerOddsRows: normalizedRows,
            footballPropsByFilter: { all: normalizedRows },
            footballPropMetaByFilter: { all: propsPayload },
            footballPropsLoading: "",
            footballPropsLoadError: "",
            error:
              jsonResult.status !== "fulfilled" && propsResult.status !== "fulfilled"
                ? "Could not load football prop detail."
                : "",
          },
        }));
        return;
      }

      const res = await api.get(`/api/fixtures/id/${fixtureId}/json`);
      setDetail((prev) => ({ ...prev, [fixtureId]: res.data || {} }));
    } catch (e) {
      setDetail((prev) => ({
        ...prev,
        [fixtureId]: { error: e?.message || "Could not load edge detail." },
      }));
    }
  };

  const ensureFootballPropMarket = useCallback(
    async (fixtureId, marketFilter = "all") => {
      const fixture = fixtureMap[String(fixtureId)] || {};
      if (inferFixtureSport(fixture) !== "football") return;
      const filterKey = String(marketFilter || "all");
      const existing = detail[fixtureId];
      if (existing?.footballPropsByFilter?.[filterKey]) return;

      setDetail((prev) => ({
        ...prev,
        [fixtureId]: {
          ...(prev[fixtureId] || {}),
          footballPropsLoading: filterKey,
          footballPropsLoadError: "",
        },
      }));

      try {
        const payload = await fetchPlayerOdds({
          fixtureId,
          market: footballMarketFilterToApiMarket(filterKey),
          limit: FOOTBALL_PROP_FETCH_LIMIT,
        });
        const normalizedRows = normalizePlayerOddsRows(payload);
        setDetail((prev) => ({
          ...prev,
          [fixtureId]: {
            ...(prev[fixtureId] || {}),
            footballPropsByFilter: {
              ...(prev[fixtureId]?.footballPropsByFilter || {}),
              [filterKey]: normalizedRows,
            },
            footballPropMetaByFilter: {
              ...(prev[fixtureId]?.footballPropMetaByFilter || {}),
              [filterKey]: payload,
            },
            footballPropsLoading: "",
            footballPropsLoadError: "",
          },
        }));
      } catch (e) {
        setDetail((prev) => ({
          ...prev,
          [fixtureId]: {
            ...(prev[fixtureId] || {}),
            footballPropsLoading: "",
            footballPropsLoadError:
              e?.response?.data?.detail ||
              e?.message ||
              "Could not load football player props for this market.",
          },
        }));
      }
    },
    [detail, fixtureMap]
  );

  useEffect(() => {
    if (!expanded || footballDetailTab !== "props") return;
    const fixture = fixtureMap[String(expanded)] || {};
    if (inferFixtureSport(fixture) !== "football") return;
    ensureFootballPropMarket(expanded, footballMarketFilter);
  }, [ensureFootballPropMarket, expanded, fixtureMap, footballDetailTab, footballMarketFilter]);

  const toggleExpand = async (fixtureId) => {
    const next = expanded === fixtureId ? null : fixtureId;
    setExpanded(next);
    if (next) {
      const fixture = fixtureMap[String(next)] || {};
      if (inferFixtureSport(fixture) === "football") {
        setFootballDetailTab("match");
      }
    }
    if (next) await ensureDetail(next);
  };

  const prefillPickForm = (fixtureId, edge = {}) => {
    const form = pickFormRefs.current[String(fixtureId)];
    if (!form) return;
    if (form.pick_type) form.pick_type.value = "game";
    if (form.player_id) form.player_id.value = "";
    if (form.player_name) form.player_name.value = "";
    if (form.player_photo) form.player_photo.value = "";
    if (form.player_team_id) form.player_team_id.value = "";
    if (form.player_team_name) form.player_team_name.value = "";
    const labels = gamePickLabels(fixtureMap[String(fixtureId)] || {}, edge.market);
    if (form.market_label) form.market_label.value = labels.market_label;
    if (form.side_label) form.side_label.value = labels.side_label;
    if (form.line) form.line.value = "";
    if (form.edge) form.edge.value = edge.edge ?? "";
    if (form.model_prob) form.model_prob.value = edge.prob ?? edge.model_prob ?? "";
    if (form.fair_price) form.fair_price.value = edge.fair_price ?? edge.fair_odds ?? "";
    if (form.market) form.market.value = edge.market || "";
    if (form.bookmaker) form.bookmaker.value = edge.bookmaker || "";
    if (form.price) {
      const price = Number(edge.price);
      form.price.value = Number.isFinite(price) ? price.toFixed(2) : "";
    }
    const rationale = edge.prefillNote || edge.plain_summary || edge.summary || edge.why || "";
    if (form.note && rationale) form.note.value = rationale;
    setLoadedFootballPropByFixture((prev) => {
      const next = { ...prev };
      delete next[String(fixtureId)];
      return next;
    });
    setSelectedFootballPropPreview((prev) => {
      const next = { ...prev };
      delete next[String(fixtureId)];
      return next;
    });
    if (form.note) form.note.focus();
  };

  const prefillFootballPropPickForm = (fixtureId, row = {}) => {
    const form = pickFormRefs.current[String(fixtureId)];
    if (!form) return;
    const note = firstDefined(row.plainSummary, row.why, "");
    if (form.pick_type) form.pick_type.value = "player_prop";
    if (form.market) form.market.value = row.market || "";
    if (form.bookmaker) form.bookmaker.value = row.bookmaker || "";
    if (form.price) {
      const price = Number(row.price);
      form.price.value = Number.isFinite(price) ? price.toFixed(2) : "";
    }
    if (form.note) form.note.value = note;
    if (form.player_name) form.player_name.value = row.playerName || row.player_name || "";
    if (form.player_id) form.player_id.value = firstDefined(row.playerId, row.player_id, "");
    if (form.player_photo) form.player_photo.value = firstDefined(row.photo, row.raw?.player_photo, row.raw?.photo, "");
    if (form.player_team_id) form.player_team_id.value = firstDefined(row.teamId, row.raw?.player_team_id, row.raw?.team_id, "");
    if (form.player_team_name) form.player_team_name.value = firstDefined(row.teamName, row.raw?.player_team_name, row.raw?.team_name, "");
    if (form.market_label) form.market_label.value = row.marketLabel || row.market_label || "";
    if (form.side_label) form.side_label.value = row.sideLabel || row.side_label || "";
    if (form.line) form.line.value = row.line ?? "";
    if (form.edge) form.edge.value = row.edge ?? "";
    if (form.model_prob) form.model_prob.value = row.modelProb ?? "";
    if (form.fair_price) form.fair_price.value = row.fairPrice ?? "";

    setLoadedFootballPropByFixture((prev) => ({ ...prev, [String(fixtureId)]: row.id || true }));
    setSelectedFootballPropPreview((prev) => ({
      ...prev,
      [String(fixtureId)]: {
        playerName: row.playerName || row.player_name || "",
        playerTeamName: firstDefined(row.teamName, row.raw?.player_team_name, row.raw?.team_name, ""),
        marketLabel: row.marketLabel || row.market_label || formatFootballPropMarket(row.market, row.market_label),
        sideLabel: row.sideLabel || row.side_label || formatFootballPropSide(row.market, row.line, row.side_label, row.market_label),
        bookmaker: row.bookmaker || "",
        price: row.price,
        photo: row.photo || row.raw?.player_photo || row.raw?.photo || null,
        fairPrice: row.fairPrice ?? null,
        modelProb: row.modelProb ?? null,
        edge: row.edge ?? null,
      },
    }));
    if (typeof form.scrollIntoView === "function") {
      form.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    if (form.note) form.note.focus();
  };

  const featureBaseballProp = (fixture, row) => {
    setPropFeatureDraft({
      fixture,
      row,
      note: propPrefillNote(row),
      stake: "1",
      isPremiumOnly: false,
    });
  };

  const submitFeaturedProp = async (e) => {
    e.preventDefault();
    if (!propFeatureDraft?.fixture || !propFeatureDraft?.row) return;
    const { fixture, row, note, isPremiumOnly, stake } = propFeatureDraft;
    setBusyPickId("adding-prop");
    try {
      const featuredSide = row.bestData || row.overData || row.underData || row;
      await createAdminPick({
        day,
        fixture_id: fixture.id,
        sport: "baseball",
        pick_type: "player_prop",
        player_id: row.playerId,
        player_name: row.playerName,
        market: row.market,
        market_label: featuredSide.marketLabel || row.marketLabel,
        side_label: featuredSide.sideLabel || row.sideLabel,
        line: row.line,
        bookmaker: row.bookmaker,
        price: featuredSide.price ?? row.overPrice ?? row.underPrice,
        edge: featuredSide.edge ?? row.bestEdge,
        model_prob: featuredSide.modelProb ?? row.modelProb,
        fair_price: featuredSide.fairPrice ?? row.fairPrice,
        note: note || propPrefillNote(row),
        stake: Number(stake) || 1,
        is_premium_only: !!isPremiumOnly,
        enforce_policy: true,
      });
      setPropFeatureDraft(null);
      await loadWorkflowData();
    } catch (e2) {
      alert(e2?.response?.data?.detail || e2?.message || "Could not create featured MLB prop pick.");
    } finally {
      setBusyPickId(null);
    }
  };

  const toggleFeaturedFixture = (fixtureId) => {
    const key = String(fixtureId);
    const fixture = fixtureMap[key];
    if (!fixture) return;
    setFeaturedNotice("");
    setSelectedFeatured((prev) => {
      const exists = prev.some((row) => String(row.id) === key);
      if (exists) {
        return prev.filter((row) => String(row.id) !== key);
      }
      if (prev.length >= 5) {
        setFeaturedNotice("Featured 5 is full. Remove one fixture before adding another.");
        return prev;
      }
      return [...prev, fixture];
    });
  };

  const moveFeaturedFixture = (fixtureId, direction) => {
    setSelectedFeatured((prev) => {
      const next = [...prev];
      const index = next.findIndex((row) => String(row.id) === String(fixtureId));
      if (index < 0) return prev;
      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= next.length) return prev;
      const swap = next[index];
      next[index] = next[targetIndex];
      next[targetIndex] = swap;
      return next;
    });
  };

  const saveFeaturedFixtures = async () => {
    setFeaturedSaving(true);
    setFeaturedErr("");
    setFeaturedNotice("");
    try {
      await updateAdminFeaturedFixtures({
        day,
        fixture_ids: selectedFeatured.map((fixture) => fixture.id).filter(Boolean).slice(0, 5),
      });
      setFeaturedNotice("Featured Community Games saved.");
      await Promise.all([loadFeaturedFixtures(), loadFeaturedPublicSummary()]);
    } catch (e) {
      setFeaturedErr(e?.response?.data?.detail || e?.message || "Could not save featured community fixtures.");
    } finally {
      setFeaturedSaving(false);
    }
  };

  const notifyFeaturedFixtures = async () => {
    setFeaturedNotifying(true);
    setFeaturedErr("");
    setFeaturedNotice("");
    try {
      const data = await notifyAdminFeaturedFixtures({ day });
      const sent = firstDefined(data?.sent, data?.notified, data?.count, null);
      setFeaturedNotice(sent == null ? "Featured 5 notification sent." : `Featured 5 notification sent to ${sent} users.`);
    } catch (e) {
      setFeaturedErr(e?.response?.data?.detail || e?.message || "Could not notify users.");
    } finally {
      setFeaturedNotifying(false);
    }
  };

  const addPick = async (fixture, form) => {
    const pickType = String(form.pick_type?.value || "game").trim().toLowerCase() || "game";
    const market = String(form.market.value || "").trim();
    const bookmaker = String(form.bookmaker.value || "").trim() || "bet365";
    const price = Number(form.price.value);
    const note = String(form.note.value || "").trim();
    const isPremiumOnly = !!form.is_premium_only.checked;
    const playerIdRaw = String(form.player_id?.value || "").trim();
    const playerName = String(form.player_name?.value || "").trim();
    const playerPhoto = String(form.player_photo?.value || "").trim();
    const playerTeamIdRaw = String(form.player_team_id?.value || "").trim();
    const playerTeamName = String(form.player_team_name?.value || "").trim();
    const marketLabel = String(form.market_label?.value || "").trim();
    const sideLabel = String(form.side_label?.value || "").trim();
    const lineRaw = String(form.line?.value || "").trim();
    const edgeRaw = String(form.edge?.value || "").trim();
    const modelProbRaw = String(form.model_prob?.value || "").trim();
    const fairPriceRaw = String(form.fair_price?.value || "").trim();

    if (!market) {
      alert("Market is required.");
      return;
    }
    if (!Number.isFinite(price)) {
      alert("Price must be numeric.");
      return;
    }

    setBusyPickId("adding");
    try {
      const gameLabels = gamePickLabels(fixture, market);
      const playerId =
        playerIdRaw && Number.isFinite(Number(playerIdRaw)) ? Number(playerIdRaw) : playerIdRaw || null;
      const playerTeamId =
        playerTeamIdRaw && Number.isFinite(Number(playerTeamIdRaw))
          ? Number(playerTeamIdRaw)
          : playerTeamIdRaw || null;
      await createAdminPick({
        day,
        fixture_id: fixture.id,
        sport: inferFixtureSport(fixture) === "football" ? "football" : fixture.sport || sport,
        pick_type: pickType,
        player_id: pickType === "player_prop" ? playerId : null,
        player_name: pickType === "player_prop" ? playerName || null : null,
        player_photo: pickType === "player_prop" ? playerPhoto || null : null,
        player_team_id: pickType === "player_prop" ? playerTeamId : null,
        player_team_name: pickType === "player_prop" ? playerTeamName || null : null,
        market,
        market_label: pickType === "player_prop" ? marketLabel || null : gameLabels.market_label,
        side_label: pickType === "player_prop" ? sideLabel || null : gameLabels.side_label,
        line: lineRaw !== "" ? Number(lineRaw) : null,
        bookmaker,
        price,
        edge: edgeRaw !== "" ? Number(edgeRaw) : null,
        model_prob: modelProbRaw !== "" ? Number(modelProbRaw) : null,
        fair_price: fairPriceRaw !== "" ? Number(fairPriceRaw) : null,
        note: note || null,
        stake: 1,
        is_premium_only: isPremiumOnly,
        enforce_policy: true,
      });
      form.reset();
      setLoadedFootballPropByFixture((prev) => {
        const next = { ...prev };
        delete next[String(fixture.id)];
        return next;
      });
      setSelectedFootballPropPreview((prev) => {
        const next = { ...prev };
        delete next[String(fixture.id)];
        return next;
      });
      await loadWorkflowData();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Could not create featured pick.");
    } finally {
      setBusyPickId(null);
    }
  };

  const removePick = async (pickId) => {
    if (!window.confirm("Remove this featured pick?")) return;
    setBusyPickId(pickId);
    try {
      await deleteAdminPick(pickId);
      await loadWorkflowData();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Could not remove pick.");
    } finally {
      setBusyPickId(null);
    }
  };

  const settlePick = async (pickId, result) => {
    setBusyPickId(pickId);
    try {
      await api.post(`/admin/picks/${pickId}/settle`, null, { params: { result } });
      await loadWorkflowData();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Could not settle pick.");
    } finally {
      setBusyPickId(null);
    }
  };

  const togglePremium = async (pick, checked) => {
    setBusyPickId(pick.pick_id);
    try {
      await createAdminPick({
        day,
        fixture_id: pick.fixture_id,
        sport: pick.sport || sport,
        pick_type: pick.pick_type || "game",
        player_id: pick.player_id || null,
        player_name: pick.player_name || null,
        player_photo: pick.player_photo || null,
        player_team_id: pick.player_team_id || null,
        player_team_name: pick.player_team_name || null,
        market: pick.market,
        market_label: pick.market_label || null,
        side_label: pick.side_label || null,
        line: pick.line ?? null,
        bookmaker: pick.bookmaker,
        price: pick.price,
        edge: pick.edge ?? null,
        model_prob: pick.model_prob ?? null,
        fair_price: pick.fair_price ?? null,
        note: pick.note || null,
        stake: 1,
        is_premium_only: checked,
        enforce_policy: true,
      });
      await loadWorkflowData();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Could not update premium flag.");
    } finally {
      setBusyPickId(null);
    }
  };

  const toggleStarPick = (pickId) => {
    const key = String(pickId);
    setStarredPickIds((prev) =>
      prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key]
    );
  };

  const clearBuilder = () => {
    setStarredPickIds([]);
    setBuilderErr("");
    setBuilderOk("");
  };

  const removeBuilderPick = (pickId) => {
    setStarredPickIds((prev) => prev.filter((id) => String(id) !== String(pickId)));
  };

  const useAllFeaturedPicks = () => {
    setStarredPickIds(
      visiblePicks
        .filter((pick) => featuredIdSet.has(String(pick.fixture_id)))
        .map((pick) => String(pick.pick_id))
    );
  };

  const builderValidation = useMemo(() => {
    if (builderPicks.length < 2) return "At least 2 picks are required.";
    for (const pick of builderPicks) {
      if (!pick.fixture_id || !pick.market || !Number.isFinite(Number(pick.price))) {
        return "Every starred pick needs fixture_id, market, and price.";
      }
    }
    if (!builderStakeUnits || !Number.isFinite(Number(builderStakeUnits))) {
      return "Stake units must be numeric.";
    }
    return "";
  }, [builderPicks, builderStakeUnits]);

  const submitAccaBuilder = async (e) => {
    e.preventDefault();
    setBuilderErr("");
    setBuilderOk("");
    if (builderValidation) {
      setBuilderErr(builderValidation);
      return;
    }

    const title = builderTitle.trim() || `${fmtDayName(day)} Featured Acca`;
    const payload = {
      day,
      sport,
      title,
      note: builderNote.trim() || null,
      stake_units: Number(builderStakeUnits),
      is_public: builderIsPublic,
      legs: builderPicks.map((pick) => ({
        fixture_id: pick.fixture_id,
        market: pick.market,
        price: Number(pick.price),
        bookmaker: pick.bookmaker,
        note: pick.note || null,
      })),
    };

    setBuilderSubmitting(true);
    try {
      const data = await createAdminAcca(payload);
      const ticketId = firstDefined(data?.ticket_id, data?.ticket?.id, data?.id);
      if (builderPublishNow && ticketId) {
        await publishAdminAcca({ ticketId, isPublic: builderIsPublic });
      }
      setBuilderOk(builderPublishNow ? "Featured acca created and published." : "Featured acca created.");
      setBuilderTitle("");
      setBuilderNote("");
      setBuilderStakeUnits("1");
      setBuilderIsPublic(true);
      setBuilderPublishNow(true);
      setStarredPickIds([]);
      await loadWorkflowData();
    } catch (e2) {
      setBuilderErr(e2?.response?.data?.detail || e2?.message || "Could not create featured acca.");
    } finally {
      setBuilderSubmitting(false);
    }
  };

  const handlePublishAcca = async (ticketId, isPublic) => {
    setBusyAccaId(ticketId);
    try {
      await publishAdminAcca({ ticketId, isPublic });
      await loadWorkflowData();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Could not publish acca.");
    } finally {
      setBusyAccaId(null);
    }
  };

  const handleDeleteAcca = async (ticketId) => {
    if (!window.confirm("Delete this featured acca?")) return;
    setBusyAccaId(ticketId);
    try {
      await deleteAdminAcca(ticketId);
      await loadWorkflowData();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Could not delete acca.");
    } finally {
      setBusyAccaId(null);
    }
  };

  const handleSettleAcca = async (acca, result) => {
    const label = String(result).toLowerCase();
    if (!window.confirm(`Mark “${acca.title || `Acca #${acca.id}`}” as ${label}? Profit will be calculated from its saved stake and combined odds.`)) return;
    setBusyAccaId(acca.id);
    try {
      await settleAdminAcca({ ticketId: acca.id, result });
      await loadWorkflowData();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Could not settle acca.");
    } finally {
      setBusyAccaId(null);
    }
  };

  const communitySummary = communityHome?.summary || {};

  return (
    <div style={{ padding: 18, color: "#e8f5ec", display: "grid", gap: 18 }}>
      <div style={{ ...PANEL, padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 24 }}>Featured Community Workflow</h2>
            <p style={{ margin: "6px 0 0", fontSize: 13, color: "#9ab3a5", maxWidth: 760 }}>
              Curate the Featured 5 first, attach featured picks to those fixtures, star the strongest plays into a daily acca, then watch how the community responds.
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#9ab3a5" }}>
              Day
              <input style={INPUT} type="date" value={day} onChange={(e) => setDay(e.target.value)} />
            </label>
            <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#9ab3a5" }}>
              Sport
              <select style={INPUT} value={sport} onChange={(e) => setSport(e.target.value)}>
                {SPORTS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" style={BTN} onClick={() => {
              loadFeaturedFixtures();
              loadFeaturedPublicSummary();
              loadWorkflowData();
            }}>
              Refresh Workflow
            </button>
          </div>
        </div>

        <div style={{ marginTop: 16, display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
          <SummaryStat label="Featured 5 Selected" value={`${selectedFeatured.length}/5`} hint="Daily homepage spotlight" />
          <SummaryStat label="Featured 5 With Picks" value={`${featuredCoverage}/5`} hint="Games currently covered by featured picks" />
          <SummaryStat label="Featured 5 Active" value={`${communityActiveCount}/5`} hint="Featured games with poll/comment/like activity" />
          <SummaryStat label="Featured Picks" value={visiblePicks.length} hint={featuredOnly ? "Filtered to Featured 5" : "All picks for the selected day"} />
          <SummaryStat label="Prediction Leaderboard" value={predictionLeaderboard.length} hint="Players with signed-in poll predictions" />
          <SummaryStat label="Community Votes" value={firstDefined(communitySummary.poll_votes_total, "—")} hint="Poll votes across community home" />
        </div>
      </div>

      <AdminFeaturedEmailPanel initialDay={day} />

      <div style={{ display: "grid", gap: 18, gridTemplateColumns: "minmax(320px, 1.05fr) minmax(0, 1.35fr)" }}>
        <section style={{ ...PANEL, padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18 }}>Featured Community Games</h3>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#8ea79b" }}>
                Curate the five fixtures that anchor the mobile community home for {fmtDayName(day)}.
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button type="button" style={BTN} onClick={loadFeaturedFixtures} disabled={featuredLoading}>
                {featuredLoading ? "Refreshing..." : "Refresh"}
              </button>
              <button type="button" style={BTN_PRIMARY} onClick={saveFeaturedFixtures} disabled={featuredSaving}>
                {featuredSaving ? "Saving..." : "Save Featured 5"}
              </button>
              <button
                type="button"
                style={BTN_PRIMARY}
                onClick={notifyFeaturedFixtures}
                disabled={featuredNotifying || featuredSaving || selectedFeatured.length === 0}
              >
                {featuredNotifying ? "Notifying..." : "Notify Users"}
              </button>
            </div>
          </div>

          {featuredErr ? <div style={{ marginTop: 12, color: "#fda4af", fontSize: 13 }}>{featuredErr}</div> : null}
          {featuredNotice ? <div style={{ marginTop: 12, color: "#bef264", fontSize: 13 }}>{featuredNotice}</div> : null}

          <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
            {Array.from({ length: 5 }).map((_, index) => {
              const fixture = selectedFeatured[index];
              return (
                <div
                  key={`slot-${index + 1}`}
                  style={{
                    ...CARD,
                    padding: 12,
                    minHeight: 78,
                    borderStyle: fixture ? "solid" : "dashed",
                    borderColor: fixture ? "rgba(49,212,139,0.36)" : "rgba(148,163,184,0.16)",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 11, color: "#9ab3a5", textTransform: "uppercase", letterSpacing: 0.7 }}>
                      Slot #{index + 1}
                    </div>
                    {fixture ? (
                      <>
                        <div style={{ marginTop: 6, fontWeight: 800 }}>
                          <TeamChip name={fixture.home_team} /> <span style={{ color: "#7f988c" }}>vs</span> <TeamChip name={fixture.away_team} align="right" />
                        </div>
                        <div style={{ marginTop: 4, fontSize: 12, color: "#8ea79b" }}>
                          {fixture.comp} • {fmtUK(fixture.kickoff_utc)}
                        </div>
                      </>
                    ) : (
                      <div style={{ marginTop: 6, fontSize: 13, color: "#789084" }}>Empty spotlight slot</div>
                    )}
                  </div>
                  {fixture ? (
                    <div style={{ display: "grid", gap: 8 }}>
                      <button type="button" style={BTN} onClick={() => moveFeaturedFixture(fixture.id, "up")} disabled={index === 0}>
                        Move Up
                      </button>
                      <button type="button" style={BTN} onClick={() => moveFeaturedFixture(fixture.id, "down")} disabled={index === selectedFeatured.length - 1}>
                        Move Down
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 16, display: "grid", gap: 10, maxHeight: 560, overflowY: "auto", paddingRight: 4 }}>
            {pickerFixtures.length ? (
              pickerFixtures.map((fixture) => {
                const order = selectedFeatured.findIndex((row) => String(row.id) === String(fixture.id)) + 1;
                return (
                  <FeaturedFixtureRow
                    key={fixture.id}
                    fixture={fixture}
                    order={order}
                    selected={order > 0}
                    onToggle={toggleFeaturedFixture}
                  />
                );
              })
            ) : (
              <div style={{ color: "#8ea79b", fontSize: 13 }}>No available fixtures returned for this day.</div>
            )}
          </div>
        </section>

        <section style={{ ...PANEL, padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18 }}>Featured Picks & Acca Builder</h3>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#8ea79b" }}>
                Manage picks as normal, then star the strongest ones into a Featured Acca builder.
              </p>
            </div>
            <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 12, color: "#cfe5d7" }}>
              <input type="checkbox" checked={featuredOnly} onChange={(e) => setFeaturedOnly(e.target.checked)} />
              Featured 5 fixtures only
            </label>
          </div>

          <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              { value: "all", label: "All" },
              { value: "game", label: "Game Picks" },
              { value: "player_prop", label: "Player Props" },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                style={pickTypeFilter === option.value ? BTN_PRIMARY : BTN}
                onClick={() => setPickTypeFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {err ? <div style={{ marginTop: 12, color: "#fda4af", fontSize: 13 }}>{err}</div> : null}
          {loading ? <div style={{ marginTop: 12, color: "#94a3b8", fontSize: 13 }}>Loading picks, fixtures, and accas…</div> : null}

          <div style={{ marginTop: 14, display: "grid", gap: 18, gridTemplateColumns: "minmax(0, 1.2fr) minmax(320px, 0.95fr)" }}>
            <div style={{ display: "grid", gap: 12, alignContent: "start" }}>
              <div style={{ display: "grid", gap: 12, maxHeight: 520, overflowY: "auto", paddingRight: 4 }}>
                {visiblePicks.length ? (
                  visiblePicks.map((pick) => {
                    const fixture = fixtureMap[String(pick.fixture_id)] || normalizeFixture(pick);
                    const isFeaturedFixture = featuredIdSet.has(String(pick.fixture_id));
                    const isStarred = starredPickIds.includes(String(pick.pick_id));
                    const resultColor =
                      pick.result === "won" ? "#4ade80" : pick.result === "lost" ? "#f87171" : pick.result === "void" ? "#cbd5e1" : "#93c5fd";

                    return (
                      <div
                        key={pick.pick_id}
                        style={{
                          ...CARD,
                          padding: 14,
                          borderColor: isStarred ? "rgba(251,191,36,0.45)" : isFeaturedFixture ? "rgba(49,212,139,0.28)" : "rgba(148,163,184,0.16)",
                          background: isStarred ? "rgba(245,158,11,0.1)" : isFeaturedFixture ? "rgba(22,185,107,0.06)" : "rgba(255,255,255,0.03)",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
                          <div>
                            <div style={{ fontWeight: 800 }}>
                              <TeamChip name={fixture.home_team} /> <span style={{ color: "#7f988c" }}>vs</span> <TeamChip name={fixture.away_team} align="right" />
                            </div>
                            <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
                              {fixture.is_world_cup ? <Badge tone="amber">WORLD CUP</Badge> : null}
                              {isFeaturedFixture ? <Badge tone="green">Community Spotlight</Badge> : null}
                              {pick.is_premium_only ? <Badge tone="amber">Premium Only</Badge> : null}
                              {isStarred ? <Badge tone="amber">Starred For Acca</Badge> : null}
                              <Badge>{fixture.comp}</Badge>
                              <Badge>{fmtUK(fixture.kickoff_utc || pick.kickoff_utc)}</Badge>
                            </div>
                          </div>
                          <button type="button" style={isStarred ? BTN_PRIMARY : BTN} onClick={() => toggleStarPick(pick.pick_id)}>
                            {isStarred ? "Starred" : "Star"}
                          </button>
                        </div>

                        <div style={{ marginTop: 10, display: "grid", gap: 6, fontSize: 13 }}>
                          {pick.pick_type === "player_prop" ? (
                            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                              <div style={{ width: 38, height: 38, borderRadius: 999, overflow: "hidden", background: "rgba(255,255,255,0.08)", flexShrink: 0 }}>
                                {pick.player_photo || pick.player_id ? (
                                  <img
                                    src={pick.player_photo || playerHeadshot(pick.player_id)}
                                    alt=""
                                    width={38}
                                    height={38}
                                    style={{ objectFit: "cover" }}
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                ) : null}
                              </div>
                              <div>
                                <div style={{ fontWeight: 800 }}>{pick.player_name || "Player Prop"}</div>
                                <div style={{ marginTop: 2, color: "#cfe2d7" }}>{featuredPickMarketLabel(pick)}</div>
                                {pick.player_team_name ? <div style={{ marginTop: 2, fontSize: 12, color: "#8ea79b" }}>{pick.player_team_name}</div> : null}
                              </div>
                            </div>
                          ) : null}
                          <div><span style={{ color: "#8ea79b" }}>Pick</span> <strong>{featuredPickMarketLabel(pick)}</strong></div>
                          <div><span style={{ color: "#8ea79b" }}>Book</span> <strong>{pick.bookmaker}</strong> <span style={{ color: "#8ea79b" }}>at</span> <strong>{Number(pick.price).toFixed(2)}</strong></div>
                          {pick.pick_type === "player_prop" ? (
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              {pick.edge != null ? <Badge tone="green">{formatEdgePercent(pick.edge)}</Badge> : null}
                              {pick.fair_price != null ? <Badge>Fair {pick.fair_price.toFixed(2)}</Badge> : null}
                              {pick.model_prob != null ? <Badge tone="blue">Model {formatModelPercent(pick.model_prob)}</Badge> : null}
                              {pick.line != null ? <Badge>Line {pick.line}</Badge> : null}
                            </div>
                          ) : null}
                          {pick.note ? <div style={{ color: "#c9ddd1" }}>{pick.note}</div> : null}
                        </div>

                        <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", fontSize: 12 }}>
                          <span style={{ color: "#8ea79b" }}>Result:</span>
                          <span style={{ color: resultColor, fontWeight: 800 }}>{pick.result || "pending"}</span>
                          <label style={{ display: "inline-flex", gap: 6, alignItems: "center", marginLeft: 8 }}>
                            <input
                              type="checkbox"
                              checked={!!pick.is_premium_only}
                              disabled={busyPickId === pick.pick_id}
                              onChange={(e) => togglePremium(pick, e.target.checked)}
                            />
                            Premium only
                          </label>
                        </div>

                        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button type="button" style={BTN} disabled={busyPickId === pick.pick_id} onClick={() => settlePick(pick.pick_id, "won")}>Won</button>
                          <button type="button" style={BTN} disabled={busyPickId === pick.pick_id} onClick={() => settlePick(pick.pick_id, "lost")}>Lost</button>
                          <button type="button" style={BTN} disabled={busyPickId === pick.pick_id} onClick={() => settlePick(pick.pick_id, "void")}>Void</button>
                          <button type="button" style={BTN} disabled={busyPickId === pick.pick_id} onClick={() => removePick(pick.pick_id)}>
                            {busyPickId === pick.pick_id ? "Removing..." : "Remove"}
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ color: "#8ea79b", fontSize: 13 }}>No featured picks found for this day.</div>
                )}
              </div>
            </div>

            <div style={{ display: "grid", gap: 14, alignContent: "start" }}>
              <form onSubmit={submitAccaBuilder} style={{ ...CARD, padding: 14, display: "grid", gap: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>Featured Acca Builder</div>
                    <div style={{ marginTop: 4, fontSize: 12, color: "#8ea79b" }}>
                      Star featured picks, review the legs, then create and optionally publish the acca.
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button type="button" style={BTN} onClick={useAllFeaturedPicks}>Use All Starred Featured 5</button>
                    <button type="button" style={BTN} onClick={clearBuilder}>Clear Builder</button>
                  </div>
                </div>

                <div style={{ display: "grid", gap: 8, maxHeight: 260, overflowY: "auto", paddingRight: 4 }}>
                  {builderPicks.length ? (
                    builderPicks.map((pick, index) => {
                      const fixture = fixtureMap[String(pick.fixture_id)] || normalizeFixture(pick);
                      return (
                        <div key={pick.pick_id} style={{ ...CARD, padding: 12 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                            <div style={{ fontWeight: 800 }}>Leg {index + 1}</div>
                            <button type="button" style={BTN} onClick={() => removeBuilderPick(pick.pick_id)}>Remove</button>
                          </div>
                          <div style={{ marginTop: 8, fontSize: 13 }}>
                            <div><TeamChip name={fixture.home_team} /> <span style={{ color: "#7f988c" }}>vs</span> <TeamChip name={fixture.away_team} align="right" /></div>
                            {pick.pick_type === "player_prop" && pick.player_name ? <div style={{ marginTop: 6, color: "#d7e7dd" }}>{pick.player_name}</div> : null}
                            <div style={{ marginTop: 6 }}><strong>{featuredPickMarketLabel(pick)}</strong></div>
                            <div style={{ marginTop: 4, color: "#a9c1b3" }}>{pick.bookmaker} • {Number(pick.price).toFixed(2)}</div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ color: "#8ea79b", fontSize: 13 }}>No starred picks yet. Star at least two picks to build the featured acca.</div>
                  )}
                </div>

                <div style={{ display: "grid", gap: 10 }}>
                  <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#9ab3a5" }}>
                    Acca title
                    <input
                      style={INPUT}
                      value={builderTitle}
                      onChange={(e) => setBuilderTitle(e.target.value)}
                      placeholder={`${fmtDayName(day)} Featured Acca`}
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#9ab3a5" }}>
                    Acca note
                    <textarea
                      style={TEXTAREA}
                      rows={3}
                      value={builderNote}
                      onChange={(e) => setBuilderNote(e.target.value)}
                      placeholder="Short note for the community home / admin context"
                    />
                  </label>
                  <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
                    <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#9ab3a5" }}>
                      Stake units
                      <input
                        style={INPUT}
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={builderStakeUnits}
                        onChange={(e) => setBuilderStakeUnits(e.target.value)}
                      />
                    </label>
                    <div style={{ ...CARD, padding: 12 }}>
                      <div style={{ fontSize: 11, color: "#9ab3a5", textTransform: "uppercase", letterSpacing: 0.6 }}>
                        Combined Odds
                      </div>
                      <div style={{ marginTop: 6, fontSize: 24, fontWeight: 800 }}>
                        {builderCombinedOdds ? `${builderCombinedOdds.toFixed(2)}x` : "—"}
                      </div>
                    </div>
                  </div>
                  <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 13 }}>
                    <input type="checkbox" checked={builderIsPublic} onChange={(e) => setBuilderIsPublic(e.target.checked)} />
                    Mark as public
                  </label>
                  <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 13 }}>
                    <input type="checkbox" checked={builderPublishNow} onChange={(e) => setBuilderPublishNow(e.target.checked)} />
                    Publish immediately after creation
                  </label>
                </div>

                {builderValidation ? <div style={{ color: "#fbbf24", fontSize: 13 }}>{builderValidation}</div> : null}
                {builderErr ? <div style={{ color: "#fda4af", fontSize: 13 }}>{builderErr}</div> : null}
                {builderOk ? <div style={{ color: "#86efac", fontSize: 13 }}>{builderOk}</div> : null}

                <button type="submit" style={BTN_PRIMARY} disabled={builderSubmitting || !!builderValidation}>
                  {builderSubmitting ? "Creating..." : "Create Featured Acca"}
                </button>
              </form>

              <div style={{ ...CARD, padding: 14 }}>
                <div style={{ fontSize: 18, fontWeight: 800 }}>Current Day Accas</div>
                <div style={{ marginTop: 4, fontSize: 12, color: "#8ea79b" }}>
                  Review, publish, or remove the accas currently attached to this day.
                </div>
                <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
                  {accas.length ? (
                    accas.map((acca) => (
                      <div key={acca.id} style={{ ...CARD, padding: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                          <div>
                            <div style={{ fontWeight: 800 }}>{acca.title}</div>
                            <div style={{ marginTop: 4, fontSize: 12, color: "#8ea79b" }}>
                              {toArray(acca.legs).length} legs • Stake {acca.stake_units}u • Combined {Number(acca.combined_price || 0).toFixed(2)}x
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {acca.is_public ? <Badge tone="green">Public</Badge> : <Badge tone="amber">Private</Badge>}
                            {acca.published_at ? <Badge tone="blue">Published</Badge> : null}
                            <Badge tone={String(acca.result || "").toUpperCase() === "WON" ? "green" : String(acca.result || "").toUpperCase() === "LOST" ? "red" : "amber"}>
                              {String(acca.result || "PENDING").toUpperCase()}
                            </Badge>
                          </div>
                        </div>
                        {acca.note ? <div style={{ marginTop: 8, fontSize: 13, color: "#cedfd4" }}>{acca.note}</div> : null}
                        <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button type="button" style={BTN} disabled={busyAccaId === acca.id} onClick={() => handlePublishAcca(acca.id, true)}>
                            {busyAccaId === acca.id ? "Working..." : "Publish"}
                          </button>
                          <button type="button" style={BTN} disabled={busyAccaId === acca.id} onClick={() => handleDeleteAcca(acca.id)}>
                            Delete
                          </button>
                          {!acca.result ? <>
                            <button type="button" style={{ ...BTN, borderColor: "#2d9b58", color: "#86efac" }} disabled={busyAccaId === acca.id} onClick={() => handleSettleAcca(acca, "WON")}>Won</button>
                            <button type="button" style={{ ...BTN, borderColor: "#9f3a46", color: "#fda4af" }} disabled={busyAccaId === acca.id} onClick={() => handleSettleAcca(acca, "LOST")}>Lost</button>
                            <button type="button" style={BTN} disabled={busyAccaId === acca.id} onClick={() => handleSettleAcca(acca, "VOID")}>Void</button>
                          </> : <span style={{ alignSelf: "center", fontSize: 12, color: Number(acca.profit || 0) >= 0 ? "#86efac" : "#fda4af" }}>
                            {Number(acca.profit || 0) >= 0 ? "+" : ""}{Number(acca.profit || 0).toFixed(2)}u
                          </span>}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ color: "#8ea79b", fontSize: 13 }}>No accas created for this day yet.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section style={{ ...PANEL, padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18 }}>Fixture Picker</h3>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "#8ea79b" }}>
              Add new featured picks against the daily fixture list. Featured Community Games are marked so the workflow stays connected.
            </p>
          </div>
          <div style={{ fontSize: 12, color: "#93ad9f" }}>
            {featuredOnly ? "Showing Featured 5 fixtures only" : "Showing all fixtures for the selected day"}
          </div>
        </div>

        {sport === "baseball" ? (
          <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            {BASEBALL_REVIEW_FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                style={baseballReviewFilter === filter.value ? BTN_PRIMARY : BTN}
                onClick={() => setBaseballReviewFilter(filter.value)}
              >
                {filter.label}
              </button>
            ))}
            <select style={{ ...INPUT, width: 180 }} value={baseballCategoryFilter} onChange={(e) => setBaseballCategoryFilter(e.target.value)}>
              {BASEBALL_CATEGORY_FILTERS.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </select>
            <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 12, color: "#cfe5d7" }}>
              <input type="checkbox" checked={baseballPositiveOnly} onChange={(e) => setBaseballPositiveOnly(e.target.checked)} />
              Positive edge only
            </label>
          </div>
        ) : null}

        <div style={{ marginTop: 14, display: "grid", gap: 12 }}>
          {visibleFixtures.length ? (
            visibleFixtures.map((fixture) => {
              const attached = picksByFixture[String(fixture.id)] || [];
              const expandedDetail = detail[fixture.id] || {};
              const bestEdges = toArray(expandedDetail.best_edges);
              const baseballContext = expandedDetail.baseballContext || {};
              const probablePitchers = baseballContext?.probable_pitchers || {};
              const marketSummary = baseballContext?.market_summary || {};
              const rawPlayerOdds = toArray(expandedDetail.playerOddsRows);
              const selectedFootballFilter = footballMarketFilter === "all" ? "all" : footballMarketFilter;
              const footballPropsByFilter = expandedDetail.footballPropsByFilter || {};
              const footballPropMetaByFilter = expandedDetail.footballPropMetaByFilter || {};
              const footballSelectedRawRows =
                inferFixtureSport(fixture) === "football"
                  ? toArray(firstDefined(footballPropsByFilter[selectedFootballFilter], footballPropsByFilter.all, rawPlayerOdds))
                  : rawPlayerOdds;
              const footballSelectedMeta =
                footballPropMetaByFilter[selectedFootballFilter] ||
                footballPropMetaByFilter.all ||
                expandedDetail.playerOddsMeta ||
                {};
              const footballPropsLoading = expandedDetail.footballPropsLoading || "";
              const footballPropsLoadError = expandedDetail.footballPropsLoadError || "";
              const selectedPropPreview = selectedFootballPropPreview[String(fixture.id)] || null;
              const mergedPlayerOdds = mergePropRows(rawPlayerOdds);
              const playerOddsMeta = expandedDetail.playerOddsMeta || {};
              const minFootballEdge = Number(footballMinEdge);
              const filteredEdges =
                bookFilter === "all"
                  ? bestEdges
                  : bestEdges.filter((edge) => String(edge.bookmaker || "").toLowerCase() === bookFilter.toLowerCase());
              const baseballGameEdges = filteredEdges
                .filter((edge) => {
                  if (!isSupportedBaseballGameEdge(edge)) return false;
                  if (bookFilter !== "all" && String(edge.bookmaker || "").toLowerCase() !== bookFilter.toLowerCase()) return false;
                  const category = baseballGameEdgeCategory(edge);
                  if (baseballPositiveOnly && Number(edge.edge || 0) <= 0) return false;
                  if (baseballCategoryFilter !== "all" && category !== baseballCategoryFilter) return false;
                  return true;
                })
                .sort((a, b) => Number(b.edge || -999) - Number(a.edge || -999));
              const baseballPropRows = mergedPlayerOdds
                .filter((row) => {
                  if (bookFilter !== "all" && String(row.bookmaker || "").toLowerCase() !== bookFilter.toLowerCase()) return false;
                  if (baseballPositiveOnly && Number(row.bestEdge || 0) <= 0) return false;
                  if (baseballCategoryFilter !== "all" && baseballPropCategory(row) !== baseballCategoryFilter) return false;
                  return true;
                });
              const topPitcherProps = baseballPropRows.filter((row) => row.groupKey.startsWith("pitcher_")).slice(0, 3);
              const topBatterProps = baseballPropRows.filter((row) => !row.groupKey.startsWith("pitcher_")).slice(0, 3);
              const propBooks = summarizeBooks(rawPlayerOdds);
              const propPlayersCount = firstDefined(
                playerOddsMeta?.players,
                playerOddsMeta?.player_count,
                new Set(rawPlayerOdds.map((row) => row.playerName)).size
              );
              const propMarketsCount = firstDefined(
                playerOddsMeta?.markets,
                playerOddsMeta?.market_count,
                new Set(rawPlayerOdds.map((row) => row.marketLabel)).size
              );
              const footballSelectedBooks = summarizeBooks(footballSelectedRawRows);
              const footballLoadedRowCount = firstDefined(
                footballSelectedMeta?.count,
                footballSelectedMeta?.rows?.length,
                footballSelectedRawRows.length
              );
              const footballLoadedMarketCount =
                selectedFootballFilter === "all"
                  ? new Set(footballSelectedRawRows.map((row) => row.footballGroup).filter(Boolean)).size
                  : footballLoadedRowCount > 0
                  ? 1
                  : 0;
              const footballPropRows = footballSelectedRawRows
                .filter((row) => {
                  if (footballMarketFilter !== "all" && row.footballGroup !== footballMarketFilter) return false;
                  if (footballBet365Only && String(row.bookmaker || "").toLowerCase() !== "bet365") return false;
                  if (footballModelOnly && row.modelProb == null) return false;
                  if (Number.isFinite(minFootballEdge) && Number(row.edge || 0) < minFootballEdge) return false;
                  if (footballPositiveOnly && Number(row.edge || 0) <= 0) return false;
                  if (!footballPositiveOnly && !footballShowNegative && Number(row.edge || 0) < 0) return false;
                  return true;
                })
                .sort((a, b) => {
                  const edgeGap = Number(b.edge || -999) - Number(a.edge || -999);
                  if (edgeGap) return edgeGap;
                  const probGap = Number(b.modelProb || -999) - Number(a.modelProb || -999);
                  if (probGap) return probGap;
                  return String(a.playerName || "").localeCompare(String(b.playerName || ""));
                });

              return (
                <div key={fixture.id} style={{ ...CARD, padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 800 }}>
                        <TeamChip name={fixture.home_team} /> <span style={{ color: "#7f988c" }}>vs</span> <TeamChip name={fixture.away_team} align="right" />
                      </div>
                      <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {fixture.is_world_cup ? <Badge tone="amber">WORLD CUP</Badge> : null}
                        {featuredIdSet.has(String(fixture.id)) ? <Badge tone="green">Community Spotlight</Badge> : null}
                        <Badge>{fixture.comp}</Badge>
                        <Badge>{fmtUK(fixture.kickoff_utc)}</Badge>
                        <Badge tone="blue">{attached.length} pick(s)</Badge>
                      </div>
                    </div>
                    <button type="button" style={BTN} onClick={() => toggleExpand(fixture.id)}>
                      {expanded === fixture.id ? "Hide Edges" : "Show Edges"}
                    </button>
                  </div>

                  {expanded === fixture.id ? (
                    <div style={{ marginTop: 12, padding: 12, borderRadius: 12, border: "1px solid rgba(148,163,184,0.18)", background: "rgba(255,255,255,0.03)" }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, color: "#93ad9f" }}>Bookmaker filter</span>
                        <select style={{ ...INPUT, width: 140 }} value={bookFilter} onChange={(e) => setBookFilter(e.target.value)}>
                          <option value="all">All</option>
                          {Array.from(
                            new Set(
                              [
                                ...toArray(expandedDetail.odds),
                                ...bestEdges,
                                ...rawPlayerOdds.map((row) => ({ bookmaker: row.bookmaker })),
                              ]
                                .map((row) => String(row.bookmaker || "").toLowerCase())
                                .filter(Boolean)
                            )
                          ).map((book) => (
                            <option key={book} value={book}>
                              {book}
                            </option>
                          ))}
                        </select>
                      </div>

                      {expandedDetail.error ? (
                        <div style={{ marginTop: 10, color: "#fda4af", fontSize: 13 }}>{expandedDetail.error}</div>
                      ) : isBaseballFixture(fixture) ? (
                        <div style={{ marginTop: 12, display: "grid", gap: 14 }}>
                          <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                            <div style={{ ...CARD, padding: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ab3a5", textTransform: "uppercase", letterSpacing: 0.7 }}>Probable Pitchers</div>
                              <div style={{ marginTop: 8, fontSize: 13, color: "#e7f5ec" }}>
                                <div>{probablePitchers.away?.full_name || probablePitchers.away?.name || fixture.away_team} <span style={{ color: "#8ea79b" }}>({fixture.away_team})</span></div>
                                <div style={{ marginTop: 4 }}>{probablePitchers.home?.full_name || probablePitchers.home?.name || fixture.home_team} <span style={{ color: "#8ea79b" }}>({fixture.home_team})</span></div>
                              </div>
                            </div>
                            <div style={{ ...CARD, padding: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ab3a5", textTransform: "uppercase", letterSpacing: 0.7 }}>Market Snapshot</div>
                              <div style={{ marginTop: 8, display: "grid", gap: 4, fontSize: 13 }}>
                                <div>Moneyline: <strong>{marketSummary?.moneyline?.away?.price ? `${fixture.away_team} ${Number(marketSummary.moneyline.away.price).toFixed(2)}` : "—"}</strong> <span style={{ color: "#8ea79b" }}>•</span> <strong>{marketSummary?.moneyline?.home?.price ? `${fixture.home_team} ${Number(marketSummary.moneyline.home.price).toFixed(2)}` : "—"}</strong></div>
                                <div>Run line: <strong>{marketSummary?.run_line?.away?.line != null ? `${fixture.away_team} ${marketSummary.run_line.away.line}` : "—"}</strong> <span style={{ color: "#8ea79b" }}>•</span> <strong>{marketSummary?.run_line?.home?.line != null ? `${fixture.home_team} ${marketSummary.run_line.home.line}` : "—"}</strong></div>
                                <div>Total: <strong>{marketSummary?.total?.over?.line != null ? `Over ${marketSummary.total.over.line}` : "—"}</strong> <span style={{ color: "#8ea79b" }}>•</span> <strong>{marketSummary?.total?.under?.line != null ? `Under ${marketSummary.total.under.line}` : "—"}</strong></div>
                              </div>
                            </div>
                            <div style={{ ...CARD, padding: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ab3a5", textTransform: "uppercase", letterSpacing: 0.7 }}>MLB Props Loaded</div>
                              <div style={{ marginTop: 8, fontSize: 13, color: "#d8e7dd" }}>
                                <div><strong>{rawPlayerOdds.length}</strong> prop prices loaded</div>
                                <div style={{ marginTop: 4 }}>{propPlayersCount || 0} players • {propMarketsCount || 0} prop categories</div>
                                <div style={{ marginTop: 4, color: "#8ea79b" }}>{propBooks ? `Books: ${propBooks}` : "Books pending"}</div>
                              </div>
                            </div>
                          </div>

                          {(baseballReviewFilter === "all" || baseballReviewFilter === "game") ? (
                            <div style={{ display: "grid", gap: 10 }}>
                              <div>
                                <div style={{ fontSize: 16, fontWeight: 800 }}>MLB Game Edges</div>
                                <div style={{ marginTop: 4, fontSize: 12, color: "#8ea79b" }}>
                                  Moneyline, totals, and run-line candidates ready to feature as standard fixture picks.
                                </div>
                              </div>
                              {baseballGameEdges.length ? (
                                <div style={{ display: "grid", gap: 10 }}>
                                  {baseballGameEdges.map((edge, index) => (
                                    <div key={`${fixture.id}-mlb-edge-${index}`} style={{ ...CARD, padding: 12 }}>
                                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
                                        <div style={{ minWidth: 0 }}>
                                          <div style={{ fontWeight: 800, color: "#f0faf4" }}>{formatBaseballGameEdgeLabel(fixture, edge)}</div>
                                          <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
                                            <Badge>{edge.bookmaker || "Book pending"}</Badge>
                                            <Badge tone="green">{formatEdgePercent(edge.edge)}</Badge>
                                            <Badge tone="blue">{Number(edge.price || 0).toFixed(2)}</Badge>
                                            {edge.model_source ? <Badge>{edge.model_source}</Badge> : null}
                                          </div>
                                          {edge.plain_summary || edge.summary || edge.why ? (
                                            <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: "rgba(31,211,122,0.06)", border: "1px solid rgba(31,211,122,0.14)", fontSize: 13, color: "#cfe2d7", lineHeight: 1.45 }}>
                                              <div style={{ marginBottom: 4, color: "#1fd37a", fontSize: 10, fontWeight: 900, letterSpacing: 1.1, textTransform: "uppercase" }}>Why this rates well</div>
                                              {edge.plain_summary || edge.summary || edge.why}
                                            </div>
                                          ) : null}
                                        </div>
                                        <button
                                          type="button"
                                          style={BTN}
                                          onClick={() =>
                                            prefillPickForm(fixture.id, {
                                              ...edge,
                                              prefillNote: edge.plain_summary || edge.summary || edge.why || "",
                                            })
                                          }
                                        >
                                          Use
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div style={{ color: "#8ea79b", fontSize: 13 }}>No MLB game edges match the current filters yet.</div>
                              )}
                            </div>
                          ) : null}

                          {(baseballReviewFilter === "all" || baseballReviewFilter === "props") ? (
                            <div style={{ display: "grid", gap: 12 }}>
                              <div>
                                <div style={{ fontSize: 16, fontWeight: 800 }}>MLB Player Prop Edges</div>
                                <div style={{ marginTop: 4, fontSize: 12, color: "#8ea79b" }}>
                                  Review batter and pitcher props from the player-odds pipeline, then feature strong plays using the existing pick model.
                                </div>
                              </div>

                              <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
                                <div style={{ ...CARD, padding: 12 }}>
                                  <div style={{ fontSize: 14, fontWeight: 800 }}>Top Pitcher Props</div>
                                  <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
                                    {topPitcherProps.length ? topPitcherProps.map((row) => (
                                      <div key={`pitcher-${row.id}`} style={{ display: "grid", gap: 2, fontSize: 13 }}>
                                        <div style={{ fontWeight: 700 }}>{row.playerName}</div>
                                        <div style={{ color: "#cfe2d7" }}>
                                          {explicitBaseballPropLabel({
                                            playerName: row.playerName,
                                            marketLabel: row.bestData?.marketLabel || row.marketLabel,
                                            sideLabel: row.bestData?.sideLabel,
                                            market: row.bestSide === "under" ? row.underData?.market : row.overData?.market,
                                            line: row.line,
                                          })}
                                        </div>
                                        <div style={{ color: edgeTone(row.bestEdge), fontWeight: 800 }}>{formatEdgePercent(row.bestEdge)} <span style={{ color: "#8ea79b", fontWeight: 500 }}>• {row.bookmaker}</span></div>
                                        {row.bestData?.plainSummary ? <div style={{ color: "#8ea79b", fontSize: 12 }}>{row.bestData.plainSummary}</div> : null}
                                      </div>
                                    )) : <div style={{ color: "#8ea79b", fontSize: 13 }}>No pitcher props loaded yet.</div>}
                                  </div>
                                </div>

                                <div style={{ ...CARD, padding: 12 }}>
                                  <div style={{ fontSize: 14, fontWeight: 800 }}>Top Batter Props</div>
                                  <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
                                    {topBatterProps.length ? topBatterProps.map((row) => (
                                      <div key={`batter-${row.id}`} style={{ display: "grid", gap: 2, fontSize: 13 }}>
                                        <div style={{ fontWeight: 700 }}>{row.playerName}</div>
                                        <div style={{ color: "#cfe2d7" }}>
                                          {explicitBaseballPropLabel({
                                            playerName: row.playerName,
                                            marketLabel: row.bestData?.marketLabel || row.marketLabel,
                                            sideLabel: row.bestData?.sideLabel,
                                            market: row.bestSide === "under" ? row.underData?.market : row.overData?.market,
                                            line: row.line,
                                          })}
                                        </div>
                                        <div style={{ color: edgeTone(row.bestEdge), fontWeight: 800 }}>{formatEdgePercent(row.bestEdge)} <span style={{ color: "#8ea79b", fontWeight: 500 }}>• {row.bookmaker}</span></div>
                                        {row.bestData?.plainSummary ? <div style={{ color: "#8ea79b", fontSize: 12 }}>{row.bestData.plainSummary}</div> : null}
                                      </div>
                                    )) : <div style={{ color: "#8ea79b", fontSize: 13 }}>No batter props loaded yet.</div>}
                                  </div>
                                </div>
                              </div>

                              {baseballPropRows.length ? (
                                <div style={{ display: "grid", gap: 10 }}>
                                  {baseballPropRows.map((row) => {
                                    const explanationOpen = !!openPropWhy[row.id];
                                    const bestSideLabel = row.bestData?.sideLabel || formatBaseballPropSide(row.bestSide === "under" ? row.underData?.market : row.overData?.market, row.line);
                                    return (
                                      <div key={row.id} style={{ ...CARD, padding: 12 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                                          <div style={{ minWidth: 0, flex: 1 }}>
                                            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                                              <div style={{ width: 40, height: 40, borderRadius: 999, overflow: "hidden", background: "rgba(255,255,255,0.08)", flexShrink: 0 }}>
                                                {row.playerId ? (
                                                  <img
                                                    src={playerHeadshot(row.playerId)}
                                                    alt=""
                                                    width={40}
                                                    height={40}
                                                    style={{ objectFit: "cover" }}
                                                    onError={(e) => {
                                                      e.currentTarget.style.display = "none";
                                                    }}
                                                  />
                                                ) : null}
                                              </div>
                                              <div style={{ minWidth: 0 }}>
                                                <div style={{ fontWeight: 800 }}>{row.playerName}</div>
                                                <div style={{ marginTop: 3, color: "#cfe2d7", fontSize: 13 }}>
                                                  {explicitBaseballPropLabel({
                                                    playerName: row.playerName,
                                                    marketLabel: row.marketLabel,
                                                    sideLabel: bestSideLabel,
                                                    market: row.bestSide === "under" ? row.underData?.market : row.overData?.market,
                                                    line: row.line,
                                                  })}
                                                </div>
                                              </div>
                                            </div>

                                            <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                                              <Badge>{row.bookmaker}</Badge>
                                              <Badge tone="green" style={{ color: edgeTone(row.bestEdge) }}>{formatEdgePercent(row.bestEdge)}</Badge>
                                              {row.bestData?.modelProb != null ? <Badge tone="blue">Model {formatModelPercent(row.bestData.modelProb)}</Badge> : null}
                                              {row.bestData?.fairPrice != null ? <Badge>Fair {row.bestData.fairPrice.toFixed(2)}</Badge> : null}
                                            </div>

                                            <div style={{ marginTop: 10, display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
                                              <div style={{ ...CARD, padding: 10, borderColor: row.overData?.edge > row.underData?.edge ? "rgba(74,222,128,0.36)" : "rgba(148,163,184,0.16)" }}>
                                                <div style={{ color: "#9ab3a5", fontSize: 11, textTransform: "uppercase" }}>{row.overData?.sideLabel || formatBaseballPropSide(row.overData?.market, row.line, "Over")}</div>
                                                <div style={{ marginTop: 4, fontWeight: 800 }}>{row.overPrice != null ? row.overPrice.toFixed(2) : "—"}</div>
                                                <div style={{ marginTop: 4, fontSize: 12, color: edgeTone(row.overData?.edge) }}>{formatEdgePercent(row.overData?.edge)}</div>
                                              </div>
                                              <div style={{ ...CARD, padding: 10, borderColor: row.underData?.edge > row.overData?.edge ? "rgba(74,222,128,0.36)" : "rgba(148,163,184,0.16)" }}>
                                                <div style={{ color: "#9ab3a5", fontSize: 11, textTransform: "uppercase" }}>{row.underData?.sideLabel || formatBaseballPropSide(row.underData?.market, row.line, "Under")}</div>
                                                <div style={{ marginTop: 4, fontWeight: 800 }}>{row.underPrice != null ? row.underPrice.toFixed(2) : "—"}</div>
                                                <div style={{ marginTop: 4, fontSize: 12, color: edgeTone(row.underData?.edge) }}>{formatEdgePercent(row.underData?.edge)}</div>
                                              </div>
                                            </div>

                                            {row.bestData?.plainSummary ? <div style={{ marginTop: 10, fontSize: 13, color: "#d7e7dd" }}>{row.bestData.plainSummary}</div> : null}
                                            {row.bestData?.why ? (
                                              <div style={{ marginTop: 8 }}>
                                                <button
                                                  type="button"
                                                  style={{ ...BTN, height: 30, padding: "0 10px", fontSize: 12 }}
                                                  onClick={() => setOpenPropWhy((prev) => ({ ...prev, [row.id]: !prev[row.id] }))}
                                                >
                                                  {explanationOpen ? "Hide Why" : "Why?"}
                                                </button>
                                                {explanationOpen ? <div style={{ marginTop: 8, fontSize: 13, color: "#bcd0c3" }}>{row.bestData.why}</div> : null}
                                              </div>
                                            ) : null}
                                            {row.bestData?.keyFactors?.length ? (
                                              <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                                                {row.bestData.keyFactors.slice(0, 4).map((factor) => (
                                                  <span key={`${row.id}-${factor}`} style={{ padding: "4px 8px", borderRadius: 999, border: "1px solid rgba(148,163,184,0.2)", background: "rgba(255,255,255,0.05)", fontSize: 11, color: "#d7e7dd" }}>
                                                    {factor}
                                                  </span>
                                                ))}
                                              </div>
                                            ) : null}
                                          </div>

                                          <button type="button" style={BTN_PRIMARY} onClick={() => featureBaseballProp(fixture, row)}>
                                            Feature Prop
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div style={{ color: "#8ea79b", fontSize: 13 }}>No MLB player prop rows match the current filters yet.</div>
                              )}
                            </div>
                          ) : null}
                        </div>
                      ) : inferFixtureSport(fixture) === "football" ? (
                        <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button
                              type="button"
                              style={footballDetailTab === "match" ? BTN_PRIMARY : BTN}
                              onClick={() => setFootballDetailTab("match")}
                            >
                              Match Markets
                            </button>
                            <button
                              type="button"
                              style={footballDetailTab === "props" ? BTN_PRIMARY : BTN}
                              onClick={() => setFootballDetailTab("props")}
                            >
                              Player Props
                            </button>
                          </div>

                          {footballDetailTab === "match" ? (
                            filteredEdges.length ? (
                              <div style={{ marginTop: 2, overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                  <thead>
                                    <tr>
                                      <th align="left" style={{ paddingBottom: 8, color: "#9ab3a5" }}>Market</th>
                                      <th align="right" style={{ paddingBottom: 8, color: "#9ab3a5" }}>Odds</th>
                                      <th align="right" style={{ paddingBottom: 8, color: "#9ab3a5" }}>Edge</th>
                                      <th align="left" style={{ paddingBottom: 8, color: "#9ab3a5" }}>Book</th>
                                      <th align="right" style={{ paddingBottom: 8, color: "#9ab3a5" }}>Use</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filteredEdges.map((edge, index) => (
                                      <tr key={`${fixture.id}-football-edge-${index}`} style={{ borderTop: "1px solid rgba(148,163,184,0.12)" }}>
                                        <td style={{ padding: "8px 0", maxWidth: 560 }}>
                                          <div style={{ fontWeight: 750 }}>{formatMarketName(edge.market)}</div>
                                          {edge.why ? (
                                            <div style={{ marginTop: 4, color: "#9ab3a5", fontSize: 12, lineHeight: 1.4 }}>{edge.why}</div>
                                          ) : null}
                                        </td>
                                        <td align="right">{Number(edge.price || 0).toFixed(2)}</td>
                                        <td align="right">{Number((Number(edge.edge || 0) * 100).toFixed(1))}%</td>
                                        <td>{edge.bookmaker}</td>
                                        <td align="right">
                                          <button
                                            type="button"
                                            style={{ ...BTN, height: 30, padding: "0 10px", fontSize: 12 }}
                                            onClick={() => prefillPickForm(fixture.id, edge)}
                                          >
                                            Use
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div style={{ color: "#8ea79b", fontSize: 13 }}>
                                No live match-market edge rows returned for this fixture.
                              </div>
                            )
                          ) : null}

                          {footballDetailTab === "props" ? (
                            <>
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                {FOOTBALL_PROP_MARKET_FILTERS.map((filter) => (
                                  <button
                                    key={filter.value}
                                    type="button"
                                    style={footballMarketFilter === filter.value ? BTN_PRIMARY : BTN}
                                    onClick={() => setFootballMarketFilter(filter.value)}
                                  >
                                    {filter.label}
                                  </button>
                                ))}
                              </div>
                              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", fontSize: 12, color: "#cfe5d7" }}>
                                <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                                  <input type="checkbox" checked={footballPositiveOnly} onChange={(e) => setFootballPositiveOnly(e.target.checked)} />
                                  Positive edges only
                                </label>
                                <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                                  <input type="checkbox" checked={footballBet365Only} onChange={(e) => setFootballBet365Only(e.target.checked)} />
                                  Bet365 only
                                </label>
                                <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                                  <input type="checkbox" checked={footballModelOnly} onChange={(e) => setFootballModelOnly(e.target.checked)} />
                                  Model probability only
                                </label>
                                <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                                  <input type="checkbox" checked={footballShowNegative} onChange={(e) => setFootballShowNegative(e.target.checked)} />
                                  Show negative edges
                                </label>
                                <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                                  <span>Min edge</span>
                                  <input
                                    style={{ ...INPUT, width: 90 }}
                                    inputMode="decimal"
                                    value={footballMinEdge}
                                    onChange={(e) => setFootballMinEdge(e.target.value)}
                                    placeholder="0.0"
                                  />
                                </label>
                              </div>
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 800 }}>Football Player Prop Monitor</div>
                            <div style={{ marginTop: 4, fontSize: 12, color: "#8ea79b" }}>
                              Review shots, shots on target, fouls, tackles, bookings, and assists. Bet365-only rows still count when the model finds an edge.
                            </div>
                            <div style={{ marginTop: 6, fontSize: 12, color: "#b7d9c5" }}>
                              Tap any player prop edge to autofill the featured pick form.
                            </div>
                          </div>

                          <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                            <div style={{ ...CARD, padding: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ab3a5", textTransform: "uppercase", letterSpacing: 0.7 }}>Football Props Loaded</div>
                              <div style={{ marginTop: 8, fontSize: 13, color: "#d8e7dd" }}>
                                <div><strong>{footballLoadedRowCount}</strong> row(s) loaded for this selection</div>
                                <div style={{ marginTop: 4 }}>{new Set(footballSelectedRawRows.map((row) => row.playerName)).size || 0} players • {footballLoadedMarketCount || 0} market group{footballLoadedMarketCount === 1 ? "" : "s"}</div>
                                <div style={{ marginTop: 4, color: "#8ea79b" }}>{footballSelectedBooks ? `Books: ${footballSelectedBooks}` : "Books pending"}</div>
                              </div>
                            </div>
                            <div style={{ ...CARD, padding: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ab3a5", textTransform: "uppercase", letterSpacing: 0.7 }}>Filter Snapshot</div>
                              <div style={{ marginTop: 8, fontSize: 13, color: "#d8e7dd", display: "grid", gap: 4 }}>
                                <div>Market: <strong>{FOOTBALL_PROP_MARKET_FILTERS.find((filter) => filter.value === footballMarketFilter)?.label || "All"}</strong></div>
                                <div>Threshold: <strong>{footballMinEdge || "0"}</strong> edge</div>
                                <div>{footballBet365Only ? "Bet365 only" : "All books"} • {footballModelOnly ? "Model rows only" : "All rows"}</div>
                                <div>Visible after filters: <strong>{footballPropRows.length}</strong></div>
                              </div>
                            </div>
                          </div>

                          {footballPropsLoading === selectedFootballFilter ? (
                            <div style={{ color: "#8ea79b", fontSize: 13 }}>
                              Loading {FOOTBALL_PROP_MARKET_FILTERS.find((filter) => filter.value === footballMarketFilter)?.label || "player props"}…
                            </div>
                          ) : footballPropsLoadError ? (
                            <div style={{ color: "#fda4af", fontSize: 13 }}>{footballPropsLoadError}</div>
                          ) : footballPropRows.length ? (
                            <div style={{ display: "grid", gap: 10 }}>
                              {footballPropRows.map((row) => {
                                const detailOpen = !!openPropWhy[row.id];
                                const tone = footballPropCardTone(row.edge);
                                const loadedIntoForm = loadedFootballPropByFixture[String(fixture.id)] === row.id;
                                return (
                                  <div
                                    key={row.id}
                                    role="button"
                                    tabIndex={0}
                                    style={{
                                      ...CARD,
                                      padding: 12,
                                      borderColor: loadedIntoForm ? "rgba(49,212,139,0.6)" : tone.borderColor,
                                      background: loadedIntoForm ? "rgba(22,185,107,0.12)" : tone.background,
                                      opacity: tone.opacity,
                                      cursor: "pointer",
                                      transform: loadedIntoForm ? "translateY(-1px)" : "none",
                                      transition: "border-color 0.18s ease, background 0.18s ease, transform 0.18s ease",
                                    }}
                                    onClick={() => prefillFootballPropPickForm(fixture.id, row)}
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        prefillFootballPropPickForm(fixture.id, row);
                                      }
                                    }}
                                  >
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                                      <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                          <div style={{ fontWeight: 800, color: "#effaf2" }}>{row.playerName}</div>
                                          <Badge>{row.teamName || "Team pending"}</Badge>
                                          <Badge tone="blue">{row.bookmaker}</Badge>
                                          <Badge tone={Number(row.edge || 0) > 0 ? "green" : "default"}>{formatEdgePercent(row.edge)}</Badge>
                                          {row.modelProb != null ? <Badge>Model {formatModelPercent(row.modelProb)}</Badge> : null}
                                          {loadedIntoForm ? <Badge tone="green">Loaded into form</Badge> : null}
                                        </div>
                                        <div style={{ marginTop: 8, display: "grid", gap: 6, gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", fontSize: 13 }}>
                                          <div><span style={{ color: "#8ea79b" }}>Market</span><div style={{ color: "#e7f5ec", fontWeight: 700 }}>{firstDefined(row.marketLabel, formatFootballPropMarket(row.market))}</div></div>
                                          <div><span style={{ color: "#8ea79b" }}>Side</span><div style={{ color: "#e7f5ec", fontWeight: 700 }}>{row.sideLabel || "—"}</div></div>
                                          <div><span style={{ color: "#8ea79b" }}>Line</span><div style={{ color: "#e7f5ec", fontWeight: 700 }}>{row.line ?? "—"}</div></div>
                                          <div><span style={{ color: "#8ea79b" }}>Price</span><div style={{ color: "#e7f5ec", fontWeight: 700 }}>{row.price != null ? row.price.toFixed(2) : "—"}</div></div>
                                          <div><span style={{ color: "#8ea79b" }}>Fair</span><div style={{ color: "#e7f5ec", fontWeight: 700 }}>{row.fairPrice != null ? row.fairPrice.toFixed(2) : "—"}</div></div>
                                          <div><span style={{ color: "#8ea79b" }}>Last seen</span><div style={{ color: "#e7f5ec", fontWeight: 700 }}>{fmtUK(row.lastSeen)}</div></div>
                                        </div>
                                        {row.plainSummary ? <div style={{ marginTop: 8, fontSize: 13, color: "#d7e7dd" }}>{row.plainSummary}</div> : null}
                                      </div>
                                      <button
                                        type="button"
                                        style={BTN}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          setOpenPropWhy((prev) => ({ ...prev, [row.id]: !prev[row.id] }));
                                        }}
                                      >
                                        {detailOpen ? "Hide Why" : "Why?"}
                                      </button>
                                    </div>

                                    {detailOpen ? (
                                      <div style={{ marginTop: 12, display: "grid", gap: 8, borderTop: "1px solid rgba(148,163,184,0.14)", paddingTop: 12 }}>
                                        {row.why ? <div style={{ fontSize: 13, color: "#cfe2d7" }}>{row.why}</div> : null}
                                        {row.expectedMean != null ? <div style={{ fontSize: 13, color: "#cfe2d7" }}>Expected mean: <strong>{row.expectedMean.toFixed(2)}</strong></div> : null}
                                        {row.keyFactors?.length ? (
                                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                            {row.keyFactors.map((factor) => (
                                              <span key={`${row.id}-${factor}`} style={{ padding: "4px 8px", borderRadius: 999, border: "1px solid rgba(148,163,184,0.2)", background: "rgba(255,255,255,0.05)", fontSize: 11, color: "#d7e7dd" }}>
                                                {factor}
                                              </span>
                                            ))}
                                          </div>
                                        ) : null}
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div style={{ color: "#8ea79b", fontSize: 13 }}>
                              No football player prop rows match the current filters for this fixture yet.
                            </div>
                          )}
                            </>
                          ) : null}
                        </div>
                      ) : filteredEdges.length ? (
                        <div style={{ marginTop: 10, overflowX: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <thead>
                              <tr>
                                <th align="left" style={{ paddingBottom: 8, color: "#9ab3a5" }}>Market</th>
                                <th align="right" style={{ paddingBottom: 8, color: "#9ab3a5" }}>Odds</th>
                                <th align="right" style={{ paddingBottom: 8, color: "#9ab3a5" }}>Edge</th>
                                <th align="left" style={{ paddingBottom: 8, color: "#9ab3a5" }}>Book</th>
                                <th align="right" style={{ paddingBottom: 8, color: "#9ab3a5" }}>Use</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredEdges.map((edge, index) => (
                                <tr key={`${fixture.id}-edge-${index}`} style={{ borderTop: "1px solid rgba(148,163,184,0.12)" }}>
                                  <td style={{ padding: "8px 0" }}>{formatMarketName(edge.market)}</td>
                                  <td align="right">{Number(edge.price || 0).toFixed(2)}</td>
                                  <td align="right">{Number((Number(edge.edge || 0) * 100).toFixed(1))}%</td>
                                  <td>{edge.bookmaker}</td>
                                  <td align="right">
                                    <button
                                      type="button"
                                      style={{ ...BTN, height: 30, padding: "0 10px", fontSize: 12 }}
                                      onClick={() => prefillPickForm(fixture.id, edge)}
                                    >
                                      Use
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div style={{ marginTop: 10, color: "#8ea79b", fontSize: 13 }}>No live edge rows returned for this fixture.</div>
                      )}
                    </div>
                  ) : null}

                  <form
                    ref={(node) => {
                      if (node) {
                        pickFormRefs.current[String(fixture.id)] = node;
                      } else {
                        delete pickFormRefs.current[String(fixture.id)];
                      }
                    }}
                    onSubmit={(e) => {
                      e.preventDefault();
                      addPick(fixture, e.currentTarget);
                    }}
                    style={{ marginTop: 14, display: "grid", gap: 10, gridTemplateColumns: "minmax(180px, 1.4fr) repeat(2, minmax(120px, 0.7fr)) minmax(180px, 1fr) auto" }}
                  >
                    <input type="hidden" name="player_photo" />
                    <input type="hidden" name="player_team_id" />
                    <input type="hidden" name="player_team_name" />
                    <input type="hidden" name="pick_type" defaultValue="game" />
                    <input type="hidden" name="player_id" />
                    <input type="hidden" name="player_name" />
                    <input type="hidden" name="market_label" />
                    <input type="hidden" name="side_label" />
                    <input type="hidden" name="line" />
                    <input type="hidden" name="edge" />
                    <input type="hidden" name="model_prob" />
                    <input type="hidden" name="fair_price" />
                    {selectedPropPreview ? (
                      <div
                        style={{
                          ...CARD,
                          gridColumn: "1 / -1",
                          padding: 10,
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          borderColor: "rgba(49,212,139,0.36)",
                          background: "rgba(22,185,107,0.08)",
                        }}
                      >
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 999,
                            overflow: "hidden",
                            background: "rgba(255,255,255,0.08)",
                            flexShrink: 0,
                          }}
                        >
                          {selectedPropPreview.photo ? (
                            <img
                              src={selectedPropPreview.photo}
                              alt=""
                              width={36}
                              height={36}
                              style={{ objectFit: "cover" }}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : null}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12, color: "#b7d9c5" }}>Selected player prop</div>
                          <div style={{ marginTop: 2, fontWeight: 800, color: "#effaf2" }}>
                            {selectedPropPreview.playerName} — {selectedPropPreview.marketLabel} — {selectedPropPreview.sideLabel}
                          </div>
                          {selectedPropPreview.playerTeamName ? (
                            <div style={{ marginTop: 2, fontSize: 12, color: "#cfe5d7" }}>
                              {selectedPropPreview.playerTeamName}
                            </div>
                          ) : null}
                          <div style={{ marginTop: 2, fontSize: 12, color: "#b7d9c5" }}>
                            {selectedPropPreview.bookmaker ? `${selectedPropPreview.bookmaker} • ` : ""}
                            {selectedPropPreview.price != null ? Number(selectedPropPreview.price).toFixed(2) : "Price pending"}
                          </div>
                          <div style={{ marginTop: 2, fontSize: 12, color: "#b7d9c5", display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {selectedPropPreview.fairPrice != null ? <span>Fair {Number(selectedPropPreview.fairPrice).toFixed(2)}</span> : null}
                            {selectedPropPreview.modelProb != null ? <span>Model {formatModelPercent(selectedPropPreview.modelProb)}</span> : null}
                            {selectedPropPreview.edge != null ? <span>Edge {formatEdgePercent(selectedPropPreview.edge)}</span> : null}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <input style={INPUT} name="market" placeholder="Market (e.g. HOME_WIN / O2.5)" />
                    <input style={INPUT} name="bookmaker" placeholder="bet365" />
                    <input style={INPUT} name="price" placeholder="1.95" inputMode="decimal" />
                    <input style={INPUT} name="note" placeholder="Optional note for the pick" />
                    <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 12 }}>
                      <input name="is_premium_only" type="checkbox" />
                      Premium
                    </label>
                    <button type="submit" style={{ ...BTN_PRIMARY, width: 150 }} disabled={busyPickId === "adding"}>
                      {busyPickId === "adding" ? "Adding..." : "Add Featured Pick"}
                    </button>
                  </form>
                </div>
              );
            })
          ) : (
            <div style={{ color: "#8ea79b", fontSize: 13 }}>No fixtures returned for this day and sport.</div>
          )}
        </div>
      </section>

      {propFeatureDraft ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(2,6,5,0.7)",
            display: "grid",
            placeItems: "center",
            padding: 18,
            zIndex: 40,
          }}
        >
          <form onSubmit={submitFeaturedProp} style={{ ...PANEL, width: "min(680px, 100%)", padding: 18, display: "grid", gap: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 20 }}>Feature MLB Player Prop</h3>
                <p style={{ margin: "6px 0 0", fontSize: 13, color: "#8ea79b" }}>
                  This now saves the structured player-prop fields directly, while keeping note editable for extra admin context.
                </p>
              </div>
              <button type="button" style={BTN} onClick={() => setPropFeatureDraft(null)}>Close</button>
            </div>

            <div style={{ ...CARD, padding: 14, display: "grid", gap: 10 }}>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ width: 46, height: 46, borderRadius: 999, overflow: "hidden", background: "rgba(255,255,255,0.08)", flexShrink: 0 }}>
                  {propFeatureDraft.row.playerId ? (
                    <img
                      src={playerHeadshot(propFeatureDraft.row.playerId)}
                      alt=""
                      width={46}
                      height={46}
                      style={{ objectFit: "cover" }}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : null}
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>{propFeatureDraft.row.playerName}</div>
                  <div style={{ marginTop: 4, fontSize: 13, color: "#cfe2d7" }}>
                    {explicitBaseballPropLabel({
                      playerName: propFeatureDraft.row.playerName,
                      marketLabel: propFeatureDraft.row.marketLabel,
                      sideLabel: propFeatureDraft.row.bestData?.sideLabel || propFeatureDraft.row.sideLabel,
                      market: propFeatureDraft.row.bestSide === "under" ? propFeatureDraft.row.underData?.market : propFeatureDraft.row.overData?.market,
                      line: propFeatureDraft.row.line,
                    })}
                  </div>
                  <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Badge>{propFeatureDraft.row.bookmaker}</Badge>
                    <Badge tone="green">{formatEdgePercent(propFeatureDraft.row.bestEdge)}</Badge>
                    {propFeatureDraft.row.bestData?.modelProb != null ? <Badge tone="blue">Model {formatModelPercent(propFeatureDraft.row.bestData.modelProb)}</Badge> : null}
                    {propFeatureDraft.row.bestData?.fairPrice != null ? <Badge>Fair {propFeatureDraft.row.bestData.fairPrice.toFixed(2)}</Badge> : null}
                  </div>
                </div>
              </div>
              {propFeatureDraft.row.bestData?.plainSummary ? <div style={{ fontSize: 13, color: "#d7e7dd" }}>{propFeatureDraft.row.bestData.plainSummary}</div> : null}
              {propFeatureDraft.row.bestData?.why ? <div style={{ fontSize: 13, color: "#a9c1b3" }}>{propFeatureDraft.row.bestData.why}</div> : null}
            </div>

            <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#9ab3a5" }}>
              Note
              <textarea
                style={TEXTAREA}
                rows={4}
                value={propFeatureDraft.note}
                onChange={(e) => setPropFeatureDraft((prev) => ({ ...prev, note: e.target.value }))}
              />
            </label>
            <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#9ab3a5", maxWidth: 180 }}>
              Stake units
              <input
                style={INPUT}
                type="number"
                min="0.1"
                step="0.1"
                value={propFeatureDraft.stake}
                onChange={(e) => setPropFeatureDraft((prev) => ({ ...prev, stake: e.target.value }))}
              />
            </label>
            <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 13 }}>
              <input
                type="checkbox"
                checked={propFeatureDraft.isPremiumOnly}
                onChange={(e) => setPropFeatureDraft((prev) => ({ ...prev, isPremiumOnly: e.target.checked }))}
              />
              Premium only
            </label>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button type="button" style={BTN} onClick={() => setPropFeatureDraft(null)}>Cancel</button>
              <button type="submit" style={BTN_PRIMARY} disabled={busyPickId === "adding-prop"}>
                {busyPickId === "adding-prop" ? "Creating..." : "Create Featured Prop Pick"}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
